/**
 * MultiTradeModal — Modal for reviewing and submitting multiple trades (KAN-125)
 *
 * Opens when a multi-trade screenshot is detected or via the Trade Grid button.
 * Displays trades in an editable HTML table with per-row validation,
 * price auto-calculation, confidence highlighting, and batch submission.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiTradeExtractionResult, ExtractedTradeRow, TradeAllocationSet, AllocationEntry } from '../../types/tradeImageCapture';
import { Bank } from '../../services/api/bankService';
import { TradingEntity } from '../../services/api/clientService';
import { GroupingsConfig } from '../../types/groupings';
import { calculateContraAmount } from '../../utils/contraAmountCalculator';
import { parseMaturity, formatMaturity } from '../../utils/maturityParser';
import { formatDateForInput } from '../../utils/dateCalculations';
import { businessCalendar } from '../../services/businessCalendar';
import { TradeBookingForm, TradeType, Direction, SettlementType, FixingReference, FixingOffset, AllocationRow, SplitRow, getDefaultFixingReference } from '../../types/tradeBooking';
import { saveDraft, submitTrade, listMultiTradeDrafts, getMultiTradeDraft, createMultiTradeDraft, updateMultiTradeDraft, deleteMultiTradeDraft, lockMultiTradeDraft, unlockMultiTradeDraft, PotentialConfirmationMatch } from '../../services/tradeBookingService';
import { clientService as mainClientService } from '../../services/clientService';
import { getMultiTradeDefaults, saveMultiTradeDefaults, MultiTradeColumnDefaults, getMailtoRecipients, saveMailtoRecipients } from '../../services/userPreferencesService';
import { MultiTradeDraftSummary } from '../../types/tradeBooking';
import { useSystemDate } from '../../hooks/useSystemDate';
import AlertModal from '../common/AlertModal';
import ConfirmDialog from '../common/ConfirmDialog';
import Toast from '../common/Toast';
import MultiTradeTable, { isRowReadyForValidation, BASE_COLUMNS, OPTION_LABELS } from './MultiTradeTable';
import FilterableSelect from './FilterableSelect';
import { getMultiTradeMailtoUrl, generateMultiTradeTableHtml, MultiTradeMailtoRow } from '../../utils/forwardMailto';
import { FileSpreadsheet, Mail, Lock } from 'lucide-react';
import { parseAmountInput } from '../../utils/amountFormatter';
import '../../styles/multiTradeModal.css';

// --- Internal row type ---

interface MultiTradeRow {
  rowIndex: number;
  selected: boolean;
  tradeType: string;
  direction: string;
  amount: number | null;
  contraAmount: number | null;
  currency1: string;
  currency2: string;
  price: number | null;
  spotPrice: number | null;
  forwardPoints: number | null;
  tradeDate: string;
  valueDate: string;
  maturity: string;
  maturityDays: number | null;
  maturityDate: string;
  paymentDate: string;
  settlementType: string;
  settlementCurrency: string;
  counterparty: string;
  ourParty: string;
  fixingReference: string;
  grouping1: string;
  grouping2: string;
  grouping3: string;
  hedgeObject: string;
  isForwardStarting: boolean;  // KAN-190
  spotFixingReference: string;  // KAN-190: spot observation reference for Forward Starting
  notes: string;
  confidence: Record<string, number>;
  priceManuallyEdited: boolean;
  valueDateManuallyEdited: boolean;
  validationErrors: string[];
  // Split mode fields
  rowType: 'trade' | 'parent' | 'split';
  groupId?: string;
  splitPercentage?: number;
}

// --- Grid mode type ---
type GridMode = 'normal' | 'allocations';

// --- Allocation column definition ---
export interface AllocationColumnDef {
  id: string;
  entityName: string;
  grouping1: string;
  grouping2: string;
  grouping3: string;
  hedgeObject: string;
  notes: string;
}

// --- Props ---

interface MultiTradeModalProps {
  clientId: string;
  extractionResult?: MultiTradeExtractionResult;
  initialDraftId?: string;
  banks: Bank[];
  tradingEntities: TradingEntity[];
  groupingsConfig: GroupingsConfig | null;
  onClose: () => void;
}

// --- Value normalization helpers ---

function normalizeTradeType(val: string): string {
  const v = val.trim().toUpperCase();
  if (v === 'FORWARD' || v === 'FWD') return 'FORWARD';
  if (v === 'SPOT') return 'SPOT';
  return val; // leave as-is for user to correct
}

function normalizeDirection(val: string): string {
  const v = val.trim().toUpperCase();
  if (v === 'BUY' || v === 'COMPRA') return 'BUY';
  if (v === 'SELL' || v === 'VENTA') return 'SELL';
  return val;
}

function normalizeSettlementType(val: string): string {
  const v = val.trim().toUpperCase().replace(/[^A-Z_]/g, '');
  if (v.includes('FISICA') || v.includes('DELIVERY') || v === 'PVP' || v === 'ENTREGAFISICA') return 'ENTREGA_FISICA';
  if (v.includes('COMPENSACION') || v.includes('NDF') || v.includes('NONDELIVERABLE')) return 'COMPENSACION';
  return val;
}

function normalizeFixingReference(val: string): string {
  if (!val) return '';
  const v = val.trim().toUpperCase().replace(/[\s.\-]+/g, '_');
  if (v.includes('USD') && (v.includes('OBS') || v.includes('OBSERV'))) return 'USD_OBS';
  if (v === 'UF') return 'UF';
  if (v.includes('BLOOMBERG') || v.includes('11AM') || v.includes('11_AM')) return 'BLOOMBERG_11AM';
  if (v === 'NA' || v === 'N_A' || v === 'N/A') return 'N/A';
  return val;
}

// --- Convert extracted row to flat row ---

function extractedToRow(row: ExtractedTradeRow): MultiTradeRow {
  const getVal = (field: string): any => row.extractedFields[field]?.value ?? null;
  const getStr = (field: string): string => {
    const v = getVal(field);
    return v != null ? String(v) : '';
  };
  const getNum = (field: string): number | null => {
    const v = getVal(field);
    if (v == null) return null;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const confidence: Record<string, number> = {};
  for (const [field, data] of Object.entries(row.extractedFields)) {
    if (data.value != null) confidence[field] = data.confidence;
  }

  return {
    rowIndex: row.rowIndex,
    selected: true,
    tradeType: normalizeTradeType(getStr('tradeType')),
    direction: normalizeDirection(getStr('direction')),
    amount: getNum('amount') != null ? Math.abs(getNum('amount')!) : null,
    contraAmount: getNum('contraAmount') != null ? Math.abs(getNum('contraAmount')!) : null,
    currency1: getStr('currency1').toUpperCase(),
    currency2: getStr('currency2').toUpperCase(),
    price: getNum('price'),
    spotPrice: getNum('spotPrice'),
    forwardPoints: getNum('forwardPoints'),
    tradeDate: getStr('tradeDate'),
    valueDate: getStr('valueDate'),
    maturity: '',
    maturityDays: null,
    maturityDate: getStr('maturityDate') || getStr('expiryDate'),
    paymentDate: getStr('paymentDate'),
    settlementType: normalizeSettlementType(getStr('settlementType')),
    settlementCurrency: getStr('settlementCurrency').toUpperCase(),
    counterparty: getStr('counterparty'),
    ourParty: getStr('ourParty'),
    fixingReference: normalizeFixingReference(getStr('fixingReference')),
    isForwardStarting: !!getVal('isForwardStarting'),  // KAN-190
    spotFixingReference: normalizeFixingReference(getStr('spotFixingReference')),  // KAN-190
    grouping1: getStr('grouping1'),
    grouping2: getStr('grouping2'),
    grouping3: getStr('grouping3'),
    hedgeObject: getStr('hedgeObject'),
    notes: getStr('notes'),
    confidence,
    priceManuallyEdited: false,
    valueDateManuallyEdited: !!getStr('valueDate'),  // preserve extracted valueDate
    validationErrors: row.validationErrors || [],
    rowType: 'trade',
  };
}

// --- Create an empty row for manual entry ---

function calcValueDateT2(tradeDateStr: string): string {
  if (!tradeDateStr) return '';
  const parts = tradeDateStr.split('-').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return '';
  const td = new Date(parts[0], parts[1] - 1, parts[2]);
  if (isNaN(td.getTime())) return '';
  return formatDateForInput(businessCalendar.addBusinessDays(td, 2));
}

function createEmptyRow(rowIndex: number, defaultTradeDate?: string, columnDefaults?: Record<string, string>): MultiTradeRow {
  const defs = columnDefaults || {};

  // Determine settlementType with cascading safety
  let settlementType = defs.settlementType || '';
  let fixingReference = defs.fixingReference || '';
  let settlementCurrency = defs.settlementCurrency || '';

  // If Entrega Física, force fixingReference=N/A and settlementCurrency='' (matches handleCellChange logic)
  if (settlementType === 'ENTREGA_FISICA') {
    fixingReference = 'N/A';
    settlementCurrency = '';
  }

  return {
    rowIndex,
    selected: true,
    tradeType: defs.tradeType === 'FORWARD_STARTING' ? 'FORWARD' : (defs.tradeType || ''),
    direction: defs.direction || '',
    amount: null,
    contraAmount: null,
    currency1: defs.currency1 || '',
    currency2: defs.currency2 || '',
    price: null,
    spotPrice: null,
    forwardPoints: null,
    tradeDate: defaultTradeDate || '',
    valueDate: defaultTradeDate
      ? (defs.tradeType === 'FORWARD_STARTING' ? calcValueDateT2(defaultTradeDate) : defaultTradeDate)
      : '',
    maturity: '',
    maturityDays: null,
    maturityDate: '',
    paymentDate: '',
    settlementType,
    settlementCurrency,
    counterparty: defs.counterparty || '',
    ourParty: defs.ourParty || '',
    fixingReference,
    grouping1: '',
    grouping2: '',
    grouping3: '',
    hedgeObject: '',
    isForwardStarting: defs.tradeType === 'FORWARD_STARTING',
    spotFixingReference: defs.tradeType === 'FORWARD_STARTING' ? 'USD_OBS' : '',
    notes: '',
    confidence: {},
    priceManuallyEdited: false,
    valueDateManuallyEdited: false,
    validationErrors: [],
    rowType: 'trade',
  };
}

// --- Validate a single row ---

function validateRow(row: MultiTradeRow): string[] {
  const errors: string[] = [];

  // Split child rows only need amount validation — all other fields are inherited from the parent
  if (row.rowType === 'split') {
    if (row.amount == null || row.amount <= 0) errors.push('Amount must be positive');
    return errors;
  }

  if (!row.tradeType) errors.push('Product is required');
  if (!row.direction) errors.push('Direction is required');
  if (row.amount == null || row.amount <= 0) errors.push('Amount must be positive');
  if (!row.currency1) errors.push('Currency 1 is required');
  if (!row.currency2) errors.push('Currency 2 is required');
  // KAN-190: price not required for Forward Starting (set to 0 automatically)
  if (!row.isForwardStarting && (row.price == null || row.price <= 0)) errors.push('Price must be positive');
  if (!row.tradeDate) errors.push('Trade Date is required');
  if (!row.counterparty) errors.push('Counterparty is required');
  if (!row.ourParty) errors.push('Our Party is required');
  if (!row.settlementType) errors.push('Settlement Type is required');
  // KAN-190: Forward Starting requires valueDate and forwardPoints
  if (row.isForwardStarting) {
    if (!row.valueDate) errors.push('Value Date is required for Forward Starting');
    if (row.forwardPoints == null) errors.push('Forward Points is required for Forward Starting');
  }
  return errors;
}

// --- Convert row to TradeBookingForm for submission ---

function rowToTradeBookingForm(row: MultiTradeRow): TradeBookingForm {
  const parseDate = (s: string): Date | null => {
    if (!s) return null;
    const parts = s.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  };

  const tradeDate = parseDate(row.tradeDate) || new Date();
  const isSpot = row.tradeType === 'SPOT';

  const formData: TradeBookingForm = {
    tradeType: row.tradeType === 'FORWARD' ? TradeType.FORWARD : TradeType.SPOT,
    direction: row.direction === 'SELL' ? Direction.SELL : Direction.BUY,
    amount: row.amount,
    currency1: row.currency1,
    price: row.price,
    spotPrice: row.spotPrice,
    forwardPoints: row.forwardPoints,
    currency2: row.currency2,
    contraAmount: row.contraAmount || calculateContraAmount(row.amount || 0, row.price || 0, row.currency2),
    tradeDate,
    valueDate: parseDate(row.valueDate),
    expiryDate: parseDate(row.maturityDate),
    paymentDate: parseDate(row.paymentDate) || parseDate(row.maturityDate),
    currency1PaymentDate: row.settlementType === 'ENTREGA_FISICA' ? (parseDate(row.paymentDate) || parseDate(row.maturityDate)) : null,
    currency2PaymentDate: row.settlementType === 'ENTREGA_FISICA' ? (parseDate(row.paymentDate) || parseDate(row.maturityDate)) : null,
    maturity: row.maturity || '',
    maturityDays: row.maturityDays,
    fixingReference: (row.fixingReference as FixingReference) || (isSpot ? FixingReference.NA : FixingReference.USD_OBS),
    spotFixingReference: (row.spotFixingReference as FixingReference) || FixingReference.USD_OBS,
    fixingOffset: isSpot ? FixingOffset.NA : (row.settlementType === 'ENTREGA_FISICA' ? FixingOffset.NA : FixingOffset.ZERO),
    settlementType: isSpot ? SettlementType.ENTREGA_FISICA : (row.settlementType === 'ENTREGA_FISICA' ? SettlementType.ENTREGA_FISICA : SettlementType.COMPENSACION),
    settlementCurrency: isSpot ? '' : (row.settlementCurrency || (row.settlementType === 'ENTREGA_FISICA' ? '' : (row.currency2 || 'CLP'))),
    isForwardStarting: false,
    forwardStartingFixingSchedule: [],
    isAverageRateForward: false,
    averageRateFixingSchedule: [],
    tradeId: null,
    notes: row.notes,
    grouping1: row.grouping1,
    grouping2: row.grouping2,
    grouping3: row.grouping3,
    hedgeObject: row.hedgeObject,
    ourParty: row.ourParty,
    ourPartyId: '',
    ourPartyTaxId: '',
    counterparty: row.counterparty,
    counterpartyId: '',
    counterpartyTaxId: '',
  };

  // KAN-190: Forward Starting overrides
  if (row.isForwardStarting) {
    formData.isForwardStarting = true;
    formData.isAverageRateForward = false;
    formData.averageRateFixingSchedule = [];
    formData.price = 0;
    formData.spotPrice = null;
    formData.contraAmount = 0;
    // Derive fixing schedule from valueDate (single entry)
    if (row.valueDate) {
      formData.forwardStartingFixingSchedule = [{ date: row.valueDate }];
      formData.valueDate = parseDate(row.valueDate);
    }
  }

  return formData;
}

// --- Component ---

const MultiTradeModal: React.FC<MultiTradeModalProps> = ({
  clientId,
  extractionResult,
  initialDraftId,
  banks,
  tradingEntities,
  groupingsConfig,
  onClose,
}) => {
  const { t, i18n } = useTranslation();

  // System date for defaulting tradeDate on new rows
  const { systemDate } = useSystemDate(clientId);

  // Convert extraction result to internal row format, or start with one empty row for manual entry
  const [trades, setTrades] = useState<MultiTradeRow[]>(() => {
    if (extractionResult && extractionResult.trades.length > 0) {
      return extractionResult.trades.map(extractedToRow);
    }
    const emptyRow = createEmptyRow(0);
    emptyRow.validationErrors = validateRow(emptyRow);
    return [emptyRow];
  });

  // Once systemDate loads, backfill empty tradeDate on rows that don't have one
  const sysDateApplied = useRef(false);
  useEffect(() => {
    if (!systemDate || sysDateApplied.current) return;
    sysDateApplied.current = true;
    setTrades(prev => prev.map(row => {
      if (row.tradeDate) return row;
      const defaultValueDate = row.isForwardStarting ? calcValueDateT2(systemDate) : systemDate;
      return { ...row, tradeDate: systemDate, valueDate: row.valueDate || defaultValueDate };
    }));
  }, [systemDate]);

  // KAN-190: Forward Starting flag (all rows are same type, guaranteed by backend)
  const isForwardStartingExtraction = extractionResult ? (trades.length > 0 && trades[0].isForwardStarting) : false;

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ current: 0, total: 0, type: '' });
  const [submitResults, setSubmitResults] = useState<{ success: number; failed: number } | null>(null);
  const [submitAlert, setSubmitAlert] = useState<{ title: string; message: string } | null>(null);

  // KAN-216: Confirmation match suggestions collected during submission
  const [confirmationMatches, setConfirmationMatches] = useState<Array<{
    tradeNumber: string;
    match: PotentialConfirmationMatch;
    bookingEntityId: string;  // KAN-241: entity the trade was booked against (may differ from parent clientId)
    status: 'pending' | 'processing' | 'success' | 'error';
  }>>([]);

  // Allocation state
  const [allocations, setAllocations] = useState<TradeAllocationSet[]>([]);
  const [allocationColumns, setAllocationColumns] = useState<AllocationColumnDef[]>([]);
  const hasAllocations = allocationColumns.length > 0;

  // Grid mode state
  const [gridMode, setGridMode] = useState<GridMode>('normal');
  const [pendingModeSwitch, setPendingModeSwitch] = useState<GridMode | null>(null);
  const [pendingCopy, setPendingCopy] = useState<{ rowIndex: number; x: number; y: number } | null>(null);
  const [pendingPaste, setPendingPaste] = useState<{
    fields: string[];
    values: (number | null)[][];
    targetRowIndexes: number[];
    mismatchRows: number[];
  } | null>(null);

  // Allocation sidebar form state
  const [allocSidebarOpen, setAllocSidebarOpen] = useState(true);
  const [newAllocEntity, setNewAllocEntity] = useState('');
  const [newAllocG1, setNewAllocG1] = useState('');
  const [newAllocG2, setNewAllocG2] = useState('');
  const [newAllocG3, setNewAllocG3] = useState('');

  // Validation display: only show errors after a submit/save attempt
  const [showValidation, setShowValidation] = useState(false);

  // Draft state
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  // Ref kept in sync with activeDraftId so the unmount cleanup useEffect can read
  // the current draft ID without a stale closure.
  const activeDraftIdRef = useRef<string | null>(null);
  const [activeDraftName, setActiveDraftName] = useState('');
  const [showDraftPicker, setShowDraftPicker] = useState(false);
  const [showDraftNamePrompt, setShowDraftNamePrompt] = useState(false);
  const [draftNameInput, setDraftNameInput] = useState('');
  const [drafts, setDrafts] = useState<MultiTradeDraftSummary[]>([]);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftToast, setDraftToast] = useState<{ message: string; type: 'success' | 'info' | 'warning' | 'error' } | null>(null);

  // Column defaults state (per-user preferences for new rows)
  const [columnDefaults, setColumnDefaults] = useState<Record<string, string>>({});
  const defaultsLoaded = useRef(false);

  // Load column defaults on mount
  useEffect(() => {
    getMultiTradeDefaults().then(defs => {
      // Convert MultiTradeColumnDefaults to Record<string, string>, stripping undefined
      const record: Record<string, string> = {};
      for (const [k, v] of Object.entries(defs)) {
        if (v) record[k] = v;
      }
      setColumnDefaults(record);
      defaultsLoaded.current = true;
    }).catch(() => {
      defaultsLoaded.current = true;
    });
  }, []);

  // Backfill initial empty rows once defaults load (only for manual entry, not extraction)
  const defaultsBackfilled = useRef(false);
  useEffect(() => {
    if (!defaultsLoaded.current || defaultsBackfilled.current || Object.keys(columnDefaults).length === 0) return;
    if (extractionResult) return; // Don't overwrite extracted data
    defaultsBackfilled.current = true;
    setTrades(prev => prev.map(row => {
      // Only backfill rows that are completely empty (no user data)
      const hasData = row.amount != null || row.counterparty || row.ourParty || row.currency1 || row.direction || row.tradeType;
      if (hasData) return row;
      const updated = createEmptyRow(row.rowIndex, row.tradeDate || undefined, columnDefaults);
      updated.validationErrors = validateRow(updated);
      return updated;
    }));
  }, [columnDefaults, extractionResult]);

  // Handle column default change (from gear popover)
  const handleColumnDefaultChange = useCallback((field: string, value: string) => {
    setColumnDefaults(prev => {
      const next = { ...prev };
      if (value) {
        next[field] = value;
      } else {
        delete next[field];
      }
      // Fire-and-forget save to Firestore
      const asDefaults: MultiTradeColumnDefaults = { ...next };
      saveMultiTradeDefaults(asDefaults).catch(() => {});
      return next;
    });
  }, []);

  // Mailto recipients state (BCC list, per-user preference)
  const [mailtoRecipients, setMailtoRecipients] = useState<string[]>([]);
  const [showMailtoSettings, setShowMailtoSettings] = useState(false);
  const [mailtoInput, setMailtoInput] = useState('');
  const mailtoPopoverRef = useRef<HTMLDivElement>(null);
  const copySplitsPopupRef = useRef<HTMLDivElement>(null);

  // Load mailto recipients on mount
  useEffect(() => {
    getMailtoRecipients().then(setMailtoRecipients).catch(() => {});
  }, []);

  // Close mailto popover on click outside
  useEffect(() => {
    if (!showMailtoSettings) return;
    const handleClick = (e: MouseEvent) => {
      if (mailtoPopoverRef.current && !mailtoPopoverRef.current.contains(e.target as Node)) {
        setShowMailtoSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMailtoSettings]);

  // Close copy-splits popup on click outside
  useEffect(() => {
    if (!pendingCopy) return;
    const handleClick = (e: MouseEvent) => {
      if (copySplitsPopupRef.current && !copySplitsPopupRef.current.contains(e.target as Node)) {
        setPendingCopy(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pendingCopy]);

  const handleAddMailtoRecipient = useCallback(() => {
    const email = mailtoInput.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (mailtoRecipients.includes(email)) { setMailtoInput(''); return; }
    const updated = [...mailtoRecipients, email];
    setMailtoRecipients(updated);
    setMailtoInput('');
    saveMailtoRecipients(updated).catch(() => {});
  }, [mailtoInput, mailtoRecipients]);

  const handleRemoveMailtoRecipient = useCallback((email: string) => {
    const updated = mailtoRecipients.filter(e => e !== email);
    setMailtoRecipients(updated);
    saveMailtoRecipients(updated).catch(() => {});
  }, [mailtoRecipients]);

  const handleExportToExcel = useCallback(async () => {
    const ExcelJS = await import('exceljs');

    const SPLIT_EDITABLE_FIELDS = new Set(['amount', 'splitPercentage', 'grouping1', 'grouping2', 'grouping3', 'hedgeObject', 'notes']);

    // Allocation columns (replicates MultiTradeTable's allocHeaders + augmentedTrades logic)
    const hasAllocations = gridMode === 'allocations' && allocationColumns.length > 0;
    const allocHeaders = hasAllocations
      ? allocationColumns.map((col, idx) => {
          const parts = [col.entityName, col.grouping1, col.grouping2, col.grouping3].filter(Boolean);
          return { label: parts.join(' / ') || `Alloc ${idx + 1}`, index: idx };
        })
      : [];
    const augmentedTrades = trades.map(trade => {
      const aug: Record<string, any> = { ...trade };
      if (hasAllocations) {
        const tradeAlloc = allocations.find(a => a.tradeRowIndex === trade.rowIndex);
        if (tradeAlloc) {
          let total = 0;
          tradeAlloc.allocations.forEach((entry, idx) => {
            const amt = entry.amount ?? null;
            aug[`alloc_${idx}`] = amt;
            if (amt != null) total += amt;
          });
          aug.alloc_total = total;
        }
      }
      return aug;
    });

    // Replicate MultiTradeTable's visibleColumns logic
    const hasData = (field: string) =>
      trades.some(r => { const v = (r as any)[field]; return v != null && v !== ''; });

    const visCols = BASE_COLUMNS.filter(col => {
      if (col.field === 'splitPercentage') return trades.some(r => r.rowType === 'split');
      if (col.alwaysShow) return true;
      if (col.showCondition === 'forwardStarting') return hasData(col.field) || isForwardStartingExtraction || trades.some(r => r.tradeType === 'FORWARD');
      if (col.showCondition === 'forwardStartingOnly') return isForwardStartingExtraction || trades.some(r => r.isForwardStarting);
      if (col.showCondition === 'compensacionOnly') return trades.some(r => r.settlementType === 'COMPENSACION');
      if (col.showCondition === 'grouping1') return hasData(col.field) || !!groupingsConfig?.grouping1Label;
      if (col.showCondition === 'grouping2') return hasData(col.field) || !!groupingsConfig?.grouping2Label;
      if (col.showCondition === 'grouping3') return hasData(col.field) || !!groupingsConfig?.grouping3Label;
      return hasData(col.field);
    });

    // Compute row labels (e.g. "1", "1.1", "1.2", "2")
    const rowLabels = new Map<number, string>();
    let tradeNum = 0;
    const splitCounters = new Map<string, number>();
    for (const row of trades) {
      if (row.rowType === 'trade' || row.rowType === 'parent') {
        tradeNum++;
        rowLabels.set(row.rowIndex, String(tradeNum));
      } else if (row.rowType === 'split' && row.groupId) {
        const parent = trades.find(r => r.rowType === 'parent' && r.groupId === row.groupId);
        const parentLabel = parent ? (rowLabels.get(parent.rowIndex) || '?') : '?';
        const count = (splitCounters.get(row.groupId) || 0) + 1;
        splitCounters.set(row.groupId, count);
        rowLabels.set(row.rowIndex, `${parentLabel}.${count}`);
      }
    }

    // Column header label
    const getColHeader = (col: typeof BASE_COLUMNS[number]): string => {
      if (col.field === '_rowNum') return '#';
      if (col.field === 'grouping1') return groupingsConfig?.grouping1Label || t(`multiTrade.columns.${col.headerKey}`);
      if (col.field === 'grouping2') return groupingsConfig?.grouping2Label || t(`multiTrade.columns.${col.headerKey}`);
      if (col.field === 'grouping3') return groupingsConfig?.grouping3Label || t(`multiTrade.columns.${col.headerKey}`);
      return t(`multiTrade.columns.${col.headerKey}`);
    };

    // Cell value with split inheritance + Forward Starting overrides
    const getCellValue = (row: MultiTradeRow, col: typeof BASE_COLUMNS[number]): string | number => {
      if (col.field === '_rowNum') return rowLabels.get(row.rowIndex) ?? '';

      let effectiveRow = row;
      if (row.rowType === 'split' && !SPLIT_EDITABLE_FIELDS.has(col.field)) {
        const parent = trades.find(r => r.rowType === 'parent' && r.groupId === row.groupId);
        if (parent) effectiveRow = parent;
      }

      if (effectiveRow.isForwardStarting && col.forwardStartingDisplay) {
        return col.forwardStartingDisplay(effectiveRow as any, t);
      }
      if (col.field === 'tradeType' && effectiveRow.isForwardStarting) {
        return OPTION_LABELS['FORWARD_STARTING'] || 'Forward (Starting)';
      }

      const val = (effectiveRow as any)[col.field];
      if (val == null || val === '') return '';
      if (col.options || col.type === 'dropdown') return OPTION_LABELS[String(val)] || String(val);
      return val;
    };

    // Build workbook
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Operaciones');

    // Header row
    const headerRow = ws.addRow([
      ...visCols.map(getColHeader),
      ...allocHeaders.map(ah => ah.label),
      ...(hasAllocations ? [t('multiTrade.columns.allocTotal')] : []),
    ]);
    headerRow.height = 22;
    headerRow.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF1E3A5F' } },
        bottom: { style: 'medium', color: { argb: 'FF0D2137' } },
        left: { style: 'thin', color: { argb: 'FF1E3A5F' } },
        right: { style: 'thin', color: { argb: 'FF1E3A5F' } },
      };
    });

    // Data rows
    augmentedTrades.forEach((augRow, rowIdx) => {
      const row = trades[rowIdx];
      const dataRow = ws.addRow([
        ...visCols.map(col => getCellValue(row, col)),
        ...allocHeaders.map(ah => { const v = augRow[`alloc_${ah.index}`]; return v != null ? v : ''; }),
        ...(hasAllocations ? [augRow.alloc_total != null ? augRow.alloc_total : ''] : []),
      ]);
      const isSplit = row.rowType === 'split';
      const isEven = rowIdx % 2 === 0;
      dataRow.height = 18;

      dataRow.eachCell((cell, colIdx) => {
        const col = visCols[colIdx - 1];
        const isNumeric = col?.type === 'numeric' || (hasAllocations && colIdx > visCols.length);
        const bgColor = isSplit
          ? (isEven ? 'FFEBF3FF' : 'FFE0EDFF')
          : (isEven ? 'FFFFFFFF' : 'FFF7F8FA');

        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        cell.font = { size: 10, italic: isSplit, color: { argb: isSplit ? 'FF3D6B99' : 'FF1A1A2E' } };
        cell.alignment = { vertical: 'middle', horizontal: isNumeric ? 'right' : 'left' };
        cell.border = {
          bottom: { style: 'hair', color: { argb: 'FFCCCCCC' } },
          left: { style: 'hair', color: { argb: 'FFCCCCCC' } },
          right: { style: 'hair', color: { argb: 'FFCCCCCC' } },
        };
        if (isNumeric && typeof cell.value === 'number') {
          const dec = col?.decimals ?? 0;
          cell.numFmt = dec > 0 ? `#,##0.${'0'.repeat(dec)}` : '#,##0';
        }
      });
    });

    // Column widths (pixel → char approximation)
    visCols.forEach((col, idx) => {
      ws.getColumn(idx + 1).width = Math.max(Math.ceil(col.width / 6.5), 8);
    });
    allocHeaders.forEach((_, idx) => {
      ws.getColumn(visCols.length + idx + 1).width = 18;
    });
    if (hasAllocations) {
      ws.getColumn(visCols.length + allocHeaders.length + 1).width = 15;
    }

    // Freeze header row
    ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2', activeCell: 'A2' }];

    // Download
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = activeDraftName ? `${activeDraftName}.xlsx` : `MultiTrade_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [trades, isForwardStartingExtraction, groupingsConfig, t, activeDraftName, gridMode, allocationColumns, allocations]);

  const handleModeChange = useCallback((newMode: GridMode) => {
    if (newMode === gridMode) return;

    // normal → allocations: trades are fully compatible, just add allocation columns
    if (gridMode === 'normal' && newMode === 'allocations') {
      setGridMode(newMode);
      // Keep trades, allocations are empty by default
      return;
    }

    // allocations → normal: trades are compatible, but allocation data will be lost
    if (gridMode === 'allocations' && newMode === 'normal') {
      const hasAllocationData = allocations.some(a => a.amount != null && a.amount > 0);
      if (hasAllocationData) {
        setPendingModeSwitch(newMode);
        return;
      }
      // No allocation data entered — switch quietly
      setAllocations([]);
      setAllocationColumns([]);
      setGridMode(newMode);
      return;
    }
  }, [gridMode, trades, allocations, columnDefaults]);

  const confirmModeSwitch = useCallback(() => {
    if (!pendingModeSwitch) return;

    // allocations → normal: keep trades, discard allocation data only
    if (gridMode === 'allocations' && pendingModeSwitch === 'normal') {
      setAllocations([]);
      setAllocationColumns([]);
      setGridMode(pendingModeSwitch);
      setPendingModeSwitch(null);
      return;
    }

    // Fallback (shouldn't be reached with current modes, but safe default)
    setTrades([createEmptyRow(0, systemDate || undefined, columnDefaults)]);
    setAllocations([]);
    setAllocationColumns([]);
    setGridMode(pendingModeSwitch);
    setPendingModeSwitch(null);
    setActiveDraftId(null);
    activeDraftIdRef.current = null;
    setActiveDraftName('');
  }, [pendingModeSwitch, gridMode, systemDate, columnDefaults]);

  // Augmented trades: merge allocation amounts into trade rows as alloc_{index} keys
  const augmentedTrades = useMemo(() => {
    if (allocations.length === 0) return trades;
    return trades.map(trade => {
      const augmented: Record<string, any> = { ...trade };
      const tradeAlloc = allocations.find(a => a.tradeRowIndex === trade.rowIndex);
      if (tradeAlloc) {
        let total = 0;
        tradeAlloc.allocations.forEach((entry, idx) => {
          const amt = entry.amount ?? null;
          augmented[`alloc_${idx}`] = amt;
          if (amt != null) total += amt;
        });
        augmented.alloc_total = total;
      }
      return augmented as MultiTradeRow;
    });
  }, [trades, allocations]);

  // Currency-specific totals (appended as regular rows after trade data)
  const pinnedBottomRowData = useMemo(() => {
    const totals: Record<string, number> = {};
    const selected = augmentedTrades.filter(t => t.selected);
    selected.forEach(t => {
      if (t.amount != null && t.currency1) {
        totals[t.currency1] = (totals[t.currency1] || 0) + t.amount;
      }
    });
    const currencies = Object.keys(totals).sort();
    if (currencies.length === 0) return [];

    // Sum allocation columns across selected rows
    const allocTotals: Record<string, number> = {};
    selected.forEach(t => {
      const row = t as Record<string, any>;
      Object.keys(row).forEach(key => {
        if (key.startsWith('alloc_') && row[key] != null) {
          allocTotals[key] = (allocTotals[key] || 0) + Number(row[key]);
        }
      });
    });

    return currencies.map((ccy, i) => {
      const totalRow: Record<string, any> = {
        rowIndex: -(i + 1),
        selected: false,
        currency1: ccy,
        amount: totals[ccy],
        direction: currencies.length === 1 ? 'Total' : `Total ${ccy}`,
        tradeType: '', currency2: '', price: null, spotPrice: null, forwardPoints: null,
        contraAmount: null, tradeDate: '', valueDate: '', maturity: '', maturityDays: null, maturityDate: '', paymentDate: '',
        settlementType: '', settlementCurrency: '', counterparty: '', ourParty: '',
        fixingReference: '', spotFixingReference: '', isForwardStarting: false,
        grouping1: '', grouping2: '', grouping3: '', hedgeObject: '', notes: '',
        confidence: {}, priceManuallyEdited: false, validationErrors: [],
      };
      // Only add allocation totals for single-currency (all rows share same alloc structure)
      if (currencies.length === 1) {
        Object.entries(allocTotals).forEach(([key, val]) => {
          totalRow[key] = val;
        });
      }
      return totalRow;
    });
  }, [augmentedTrades]);

  // Validation counts (only count rows that are ready for validation)
  const errorCount = useMemo(
    () => trades.filter(t => t.selected && isRowReadyForValidation(t) && t.validationErrors.length > 0).length,
    [trades]
  );

  const selectedCount = useMemo(
    () => trades.filter(t => t.selected).length,
    [trades]
  );

  // Dropdown options
  const counterpartyNames = useMemo(() => banks.map(b => b.name).sort(), [banks]);
  const ourPartyNames = useMemo(() => tradingEntities.map(e => e.name).sort(), [tradingEntities]);

  // --- Cell value changed handler ---
  const handleCellChange = useCallback((rowIndex: number, field: string, value: any) => {
    setTrades(prev => {
      const targetRow = prev.find(t => t.rowIndex === rowIndex);
      // Look up parent amount for split percentage sync
      const parentRow = targetRow?.rowType === 'split' && targetRow.groupId
        ? prev.find(t => t.rowType === 'parent' && t.groupId === targetRow.groupId)
        : null;

      return prev.map(row => {
      if (row.rowIndex !== rowIndex) return row;

      let updated = { ...row, [field]: value };

      // Handle tradeType → Forward Starting mapping
      if (field === 'tradeType') {
        if (value === 'FORWARD_STARTING') {
          updated.tradeType = 'FORWARD';
          updated.isForwardStarting = true;
          updated.price = 0;
          updated.spotPrice = null;
          updated.contraAmount = 0;
          updated.priceManuallyEdited = false;
          if (!updated.spotFixingReference) updated.spotFixingReference = 'USD_OBS';
        } else {
          updated.isForwardStarting = false;
          // KAN-203: Clear stale values set when entering Forward Starting mode
          updated.contraAmount = null;
          updated.price = null;
          updated.spotPrice = null;
          updated.priceManuallyEdited = false;
        }
      }

      // Handle ourParty change → reset all groupings (entity context changed)
      if (field === 'ourParty') {
        updated.grouping1 = '';
        updated.grouping2 = '';
        updated.grouping3 = '';
      }

      // Handle grouping1 change → reset child groupings in subgrouping mode
      if (field === 'grouping1' && groupingsConfig?.grouping2Mode === 'subgrouping') {
        updated.grouping2 = '';
        updated.grouping3 = '';
      }

      // Handle grouping2 change → reset grouping3 in subgrouping mode
      if (field === 'grouping2' && groupingsConfig?.grouping3Mode === 'subgrouping') {
        updated.grouping3 = '';
      }

      // Handle settlementType change → set fixingReference and fixingOffset
      if (field === 'settlementType') {
        if (value === 'ENTREGA_FISICA') {
          updated.fixingReference = 'N/A';
          updated.settlementCurrency = '';
        } else {
          // Compensación: restore default fixing reference and settlement currency
          updated.fixingReference = getDefaultFixingReference(updated.currency1, updated.currency2) as string;
          if (!updated.settlementCurrency) {
            updated.settlementCurrency = updated.currency2 || 'CLP';
          }
        }
      }

      // Triangular price interaction — KAN-190: skip for Forward Starting
      // Price = SpotPrice + ForwardPoints (all three directions)
      // Round to 10 decimal places to avoid IEEE 754 floating-point artifacts
      const roundPrice = (n: number) => Math.round(n * 1e10) / 1e10;
      if (!updated.isForwardStarting) {
        if (field === 'spotPrice') {
          if (updated.spotPrice != null && updated.forwardPoints != null) {
            updated.price = roundPrice(updated.spotPrice + updated.forwardPoints);
          } else if (updated.spotPrice != null && updated.price != null) {
            updated.forwardPoints = roundPrice(updated.price - updated.spotPrice);
          }
          updated.priceManuallyEdited = false;
        }
        if (field === 'forwardPoints') {
          if (updated.spotPrice != null && updated.forwardPoints != null) {
            updated.price = roundPrice(updated.spotPrice + updated.forwardPoints);
          } else if (updated.forwardPoints != null && updated.price != null) {
            updated.spotPrice = roundPrice(updated.price - updated.forwardPoints);
          }
          updated.priceManuallyEdited = false;
        }
        if (field === 'price') {
          updated.priceManuallyEdited = true;
          if (updated.price != null && updated.spotPrice != null) {
            updated.forwardPoints = roundPrice(updated.price - updated.spotPrice);
          } else if (updated.price != null && updated.forwardPoints != null) {
            updated.spotPrice = roundPrice(updated.price - updated.forwardPoints);
          }
        }
      }

      // --- Auto-populate valueDate when tradeDate changes ---
      if (field === 'tradeDate' && updated.tradeDate) {
        if (updated.isForwardStarting) {
          // Forward Starting: valueDate = T+2, unless user has manually edited it
          if (!updated.valueDateManuallyEdited) {
            updated.valueDate = calcValueDateT2(updated.tradeDate);
          }
        } else {
          // Regular: valueDate = tradeDate (same day)
          updated.valueDate = updated.tradeDate;
        }
      }

      // --- Track manual valueDate edits ---
      if (field === 'valueDate') {
        updated.valueDateManuallyEdited = true;
      }

      // --- Maturity / expiryDate interaction ---
      // Base date: valueDate for Forward Starting, tradeDate for regular Forward
      const getBaseDate = (r: MultiTradeRow): Date | null => {
        const dateStr = r.isForwardStarting ? r.valueDate : r.tradeDate;
        if (!dateStr) return null;
        const parts = dateStr.split('-').map(Number);
        if (parts.length !== 3 || parts.some(isNaN)) return null;
        const [y, m, d] = parts;
        const date = new Date(y, m - 1, d);
        return isNaN(date.getTime()) ? null : date;
      };

      // When maturity text changes → parse and calc maturityDate (snapped to next business day)
      if (field === 'maturity') {
        const parsed = parseMaturity(updated.maturity);
        if (parsed.success) {
          updated.maturityDays = parsed.days;
          const base = getBaseDate(updated);
          if (base) {
            const rawExpiry = new Date(base);
            rawExpiry.setDate(rawExpiry.getDate() + parsed.days);
            const adjustedExpiry = businessCalendar.getNextBusinessDay(rawExpiry);
            updated.maturityDate = formatDateForInput(adjustedExpiry);
          }
        } else {
          updated.maturityDays = null;
        }
      }

      // When maturityDate changes directly → back-calculate maturity/maturityDays
      if (field === 'maturityDate' && updated.maturityDate) {
        const base = getBaseDate(updated);
        if (base) {
          const parts = updated.maturityDate.split('-').map(Number);
          if (parts.length === 3 && !parts.some(isNaN)) {
            const expiry = new Date(parts[0], parts[1] - 1, parts[2]);
            if (!isNaN(expiry.getTime())) {
              const diffMs = expiry.getTime() - base.getTime();
              const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
              if (days > 0) {
                updated.maturityDays = days;
                updated.maturity = formatMaturity(days);
              }
            }
          }
        }
      }

      // When base date changes (tradeDate or valueDate) → recalculate maturityDate if maturityDays set
      if ((field === 'tradeDate' || field === 'valueDate') && updated.maturityDays != null && updated.maturityDays > 0) {
        const base = getBaseDate(updated);
        if (base) {
          const rawExpiry = new Date(base);
          rawExpiry.setDate(rawExpiry.getDate() + updated.maturityDays);
          const adjustedExpiry = businessCalendar.getNextBusinessDay(rawExpiry);
          updated.maturityDate = formatDateForInput(adjustedExpiry);
        }
      }

      // Split amount ↔ percentage sync
      if (updated.rowType === 'split' && parentRow?.amount) {
        if (field === 'amount' && updated.amount != null) {
          updated.splitPercentage = Math.round((updated.amount / parentRow.amount) * 10000) / 100;
        } else if (field === 'splitPercentage' && updated.splitPercentage != null) {
          updated.amount = Math.round(parentRow.amount * updated.splitPercentage / 100);
        }
      }

      // Re-validate
      updated.validationErrors = validateRow(updated);

      return updated;
    });
    });
  }, [groupingsConfig]);

  // --- Allocation cell change handler ---
  const handleAllocCellChange = useCallback((rowIndex: number, allocIdx: number, value: number | null) => {
    setAllocations(prev => prev.map(tradeAlloc => {
      if (tradeAlloc.tradeRowIndex !== rowIndex) return tradeAlloc;
      return {
        ...tradeAlloc,
        allocations: tradeAlloc.allocations.map((a, i) =>
          i === allocIdx ? { ...a, amount: value ?? undefined } : a
        ),
      };
    }));
  }, []);

  // --- Copy allocation value down to last row ---
  const handleAllocCopyDown = useCallback((rowIndex: number, allocIdx: number) => {
    if (trades.length < 2) return;
    const lastRow = trades[trades.length - 1];
    if (lastRow.rowIndex === rowIndex) return;
    const sourceAlloc = allocations.find(a => a.tradeRowIndex === rowIndex);
    if (!sourceAlloc) return;
    const value = sourceAlloc.allocations[allocIdx]?.amount;
    if (value == null) return;
    handleAllocCellChange(lastRow.rowIndex, allocIdx, value);
  }, [trades, allocations, handleAllocCellChange]);

  // --- Add allocation column ---
  const handleAddAllocColumn = useCallback((entityName: string, g1: string, g2: string, g3: string) => {
    const colId = `alloc_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const newCol: AllocationColumnDef = { id: colId, entityName, grouping1: g1, grouping2: g2, grouping3: g3, hedgeObject: '', notes: '' };
    setAllocationColumns(prev => [...prev, newCol]);
    // Add an empty allocation entry for this column on every existing trade
    setAllocations(prev => {
      const tradeIndices = trades.map(t => t.rowIndex);
      const newEntry: AllocationEntry = { entityName, grouping1: g1, grouping2: g2, grouping3: g3, hedgeObject: '', notes: '' };
      // If trade already has an allocation set, append; otherwise create one
      const existingSets = new Map(prev.map(a => [a.tradeRowIndex, a]));
      return tradeIndices.map(idx => {
        const existing = existingSets.get(idx);
        if (existing) {
          return { ...existing, allocations: [...existing.allocations, newEntry] };
        }
        // Create new allocation set: fill empty entries for all prior columns, plus this one
        const emptyEntries: AllocationEntry[] = allocationColumns.map(c => ({
          entityName: c.entityName, grouping1: c.grouping1, grouping2: c.grouping2, grouping3: c.grouping3,
          hedgeObject: c.hedgeObject, notes: c.notes,
        }));
        return { tradeRowIndex: idx, allocations: [...emptyEntries, newEntry] };
      });
    });
  }, [trades, allocationColumns]);

  // --- Grouping options for allocation sidebar ---
  const allocSidebarGroupingOptions = useMemo(() => {
    if (!newAllocEntity || !groupingsConfig) return { g1: [] as string[], g2: [] as string[], g3: [] as string[] };
    const entity = tradingEntities.find(e => e.name === newAllocEntity);
    if (!entity) return { g1: [] as string[], g2: [] as string[], g3: [] as string[] };
    const mapping = groupingsConfig.entityMappings.find(m => m.entityId === entity.id);
    if (!mapping) return { g1: [] as string[], g2: [] as string[], g3: [] as string[] };

    const g1 = groupingsConfig.grouping1Values
      .filter(v => v.isActive && mapping.grouping1ValueIds.includes(v.id))
      .map(v => v.name);

    let g2: string[] = [];
    if (groupingsConfig.grouping2Mode === 'subgrouping') {
      const selectedG1 = groupingsConfig.grouping1Values.find(v => v.name === newAllocG1);
      if (selectedG1) {
        const allowedIds = mapping.g1ToG2Mappings[selectedG1.id] || [];
        g2 = groupingsConfig.grouping2Values
          .filter(v => v.isActive && allowedIds.includes(v.id))
          .map(v => v.name);
      }
    } else {
      g2 = groupingsConfig.grouping2Values
        .filter(v => v.isActive && mapping.grouping2ValueIds.includes(v.id))
        .map(v => v.name);
    }

    let g3: string[] = [];
    if (groupingsConfig.grouping3Mode === 'subgrouping') {
      const selectedG2 = groupingsConfig.grouping2Values.find(v => v.name === newAllocG2);
      if (selectedG2) {
        const allowedIds = mapping.g2ToG3Mappings[selectedG2.id] || [];
        g3 = groupingsConfig.grouping3Values
          .filter(v => v.isActive && allowedIds.includes(v.id))
          .map(v => v.name);
      }
    } else {
      g3 = groupingsConfig.grouping3Values
        .filter(v => v.isActive && mapping.grouping3ValueIds.includes(v.id))
        .map(v => v.name);
    }

    return { g1, g2, g3 };
  }, [newAllocEntity, newAllocG1, newAllocG2, groupingsConfig, tradingEntities]);

  // --- Submit allocation column from sidebar ---
  const handleSubmitAllocColumn = useCallback(() => {
    if (!newAllocEntity) return;
    handleAddAllocColumn(newAllocEntity, newAllocG1, newAllocG2, newAllocG3);
    setNewAllocEntity('');
    setNewAllocG1('');
    setNewAllocG2('');
    setNewAllocG3('');
  }, [newAllocEntity, newAllocG1, newAllocG2, newAllocG3, handleAddAllocColumn]);

  // --- Remove allocation column ---
  const handleRemoveAllocColumn = useCallback((columnIndex: number) => {
    setAllocationColumns(prev => prev.filter((_, i) => i !== columnIndex));
    setAllocations(prev => prev.map(tradeAlloc => ({
      ...tradeAlloc,
      allocations: tradeAlloc.allocations.filter((_, i) => i !== columnIndex),
    })));
  }, []);

  // --- Update allocation column hedge object / notes ---
  const handleUpdateAllocColumn = useCallback((columnIndex: number, hedgeObject: string, notes: string) => {
    setAllocationColumns(prev => prev.map((col, i) =>
      i === columnIndex ? { ...col, hedgeObject, notes } : col
    ));
    setAllocations(prev => prev.map(tradeAlloc => ({
      ...tradeAlloc,
      allocations: tradeAlloc.allocations.map((entry, i) =>
        i === columnIndex ? { ...entry, hedgeObject, notes } : entry
      ),
    })));
  }, []);

  // --- Row selection toggle ---
  const toggleRowSelection = useCallback((rowIndex: number) => {
    setTrades(prev => prev.map(row =>
      row.rowIndex === rowIndex ? { ...row, selected: !row.selected } : row
    ));
  }, []);

  const toggleSelectAll = useCallback(() => {
    const allSelected = trades.every(t => t.selected);
    setTrades(prev => prev.map(row => ({ ...row, selected: !allSelected })));
  }, [trades]);

  // --- Add empty row for manual entry ---
  const handleAddRow = useCallback(() => {
    setTrades(prev => {
      const maxIndex = prev.length > 0 ? Math.max(...prev.map(r => r.rowIndex)) : -1;
      const newRow = createEmptyRow(maxIndex + 1, systemDate || undefined, columnDefaults);
      newRow.validationErrors = validateRow(newRow);
      // If allocation columns exist, add an empty allocation set for this row
      if (allocationColumns.length > 0) {
        const emptyEntries: AllocationEntry[] = allocationColumns.map(c => ({
          entityName: c.entityName, grouping1: c.grouping1, grouping2: c.grouping2, grouping3: c.grouping3,
          hedgeObject: c.hedgeObject, notes: c.notes,
        }));
        setAllocations(prevAllocs => [...prevAllocs, { tradeRowIndex: newRow.rowIndex, allocations: emptyEntries }]);
      }
      return [...prev, newRow];
    });
  }, [systemDate, allocationColumns, columnDefaults]);

  // --- Copy a single field value down to the last row ---
  // Find the appropriate target row for copy-down: if the last row is a split child
  // and the field isn't split-editable, target the last parent/trade row instead.
  const SPLIT_EDITABLE_FIELDS = ['amount', 'splitPercentage', 'grouping1', 'grouping2', 'grouping3', 'hedgeObject', 'notes'];
  const findCopyDownTarget = useCallback((field: string) => {
    for (let i = trades.length - 1; i >= 0; i--) {
      const row = trades[i];
      if (row.rowType === 'split' && !SPLIT_EDITABLE_FIELDS.includes(field)) continue;
      return row;
    }
    return null;
  }, [trades]);

  const handleCopyFieldDown = useCallback((rowIndex: number, field: string) => {
    if (trades.length < 2) return;
    const sourceRow = trades.find(t => t.rowIndex === rowIndex);
    const targetRow = findCopyDownTarget(field);
    if (!sourceRow || !targetRow || sourceRow.rowIndex === targetRow.rowIndex) return;
    const value = (sourceRow as any)[field];
    if (value == null || value === '') return;
    // Use handleCellChange so all side effects (price calc, maturity, etc.) fire
    handleCellChange(targetRow.rowIndex, field, value);
  }, [trades, handleCellChange, findCopyDownTarget]);

  // --- Copy a date field to the last row with an offset (e.g. +7 or +30 days) ---
  const handleCopyDateOffset = useCallback((rowIndex: number, field: string, offsetDays: number) => {
    if (trades.length < 2) return;
    const sourceRow = trades.find(t => t.rowIndex === rowIndex);
    const targetRow = findCopyDownTarget(field);
    if (!sourceRow || !targetRow || sourceRow.rowIndex === targetRow.rowIndex) return;
    const dateStr = (sourceRow as any)[field];
    if (!dateStr) return;
    const parts = dateStr.split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return;
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    if (isNaN(date.getTime())) return;
    date.setDate(date.getDate() + offsetDays);
    const adjustedDate = businessCalendar.getNextBusinessDay(date);
    const offsetDate = formatDateForInput(adjustedDate);
    handleCellChange(targetRow.rowIndex, field, offsetDate);
  }, [trades, handleCellChange, findCopyDownTarget]);

  // --- Paste values from clipboard ---
  const PRICE_FIELD_ORDER: string[] = ['spotPrice', 'forwardPoints', 'price'];

  const applyPasteValues = useCallback((
    fields: string[],
    values: (number | null)[][],
    targetRowIndexes: number[],
    mismatchRows: number[],
  ) => {
    // Blur the active cell so the NumericCell exits editing mode and shows the new value
    (document.activeElement as HTMLElement)?.blur();

    // Detect allocation paste (fields like alloc_0, alloc_1, ...)
    const isAllocPaste = fields.length > 0 && fields[0].startsWith('alloc_');

    if (isAllocPaste) {
      // Apply via handleAllocCellChange for each column
      for (let r = 0; r < values.length; r++) {
        const rowIndex = targetRowIndexes[r];
        for (let c = 0; c < fields.length; c++) {
          const val = values[r][c];
          if (val != null) {
            const allocIdx = parseInt(fields[c].replace('alloc_', ''), 10);
            handleAllocCellChange(rowIndex, allocIdx, val);
          }
        }
      }
    } else {
      const allThreeFields = fields.length === 3
        && fields[0] === 'spotPrice' && fields[1] === 'forwardPoints' && fields[2] === 'price';

      if (allThreeFields) {
        // When pasting all 3 price columns, set them directly to avoid triangulation
        // overwriting pasted values. handleCellChange would recalculate each field
        // as it's set, but we want the exact pasted values to stick.
        setTrades(prev => prev.map(row => {
          const rIdx = targetRowIndexes.indexOf(row.rowIndex);
          if (rIdx === -1) return row;
          const [spot, fwdPts, price] = values[rIdx];
          const updated = { ...row };
          if (spot != null) updated.spotPrice = spot;
          if (fwdPts != null) updated.forwardPoints = fwdPts;
          if (price != null) {
            updated.price = price;
            updated.priceManuallyEdited = true;
          }
          updated.validationErrors = validateRow(updated);
          return updated;
        }));
      } else {
        // For amount, or 1-2 price columns, use handleCellChange so triangulation auto-fills
        for (let r = 0; r < values.length; r++) {
          const rowIndex = targetRowIndexes[r];
          for (let c = 0; c < fields.length; c++) {
            const val = values[r][c];
            if (val != null) {
              handleCellChange(rowIndex, fields[c], val);
            }
          }
        }
      }
    }

    // Show result toast
    if (mismatchRows.length > 0) {
      const rowNums = mismatchRows.map(i => i + 1).join(', ');
      setDraftToast({
        message: `${t('multiTrade.paste.success', { count: values.length })}. ${t('multiTrade.paste.priceMismatch', { rows: rowNums })}`,
        type: 'warning',
      });
    } else {
      setDraftToast({ message: t('multiTrade.paste.success', { count: values.length }), type: 'success' });
    }
    setPendingPaste(null);
  }, [handleCellChange, handleAllocCellChange, t]);

  const handlePasteValues = useCallback((field: string, rowIndex: number, text: string) => {
    // Parse clipboard: rows by newline, columns by tab
    const rawRows = text.split(/\r?\n/).filter(line => line.trim() !== '');
    if (rawRows.length === 0) return;

    const parsedRows = rawRows.map(line => line.split('\t').map(cell => cell.trim()));
    const colCount = parsedRows[0].length;

    // --- Maturity paste: positive integer days only, no fractions ---
    if (field === 'maturity') {
      const parseErrors: { row: number; value: string }[] = [];
      const parsedDays: (number | null)[] = parsedRows.map((cols, rowIdx) => {
        const raw = cols[0] || '';
        if (raw === '') return null;
        const n = Number(raw);
        if (!Number.isInteger(n) || n <= 0) {
          parseErrors.push({ row: rowIdx + 1, value: raw });
          return null;
        }
        return n;
      });

      if (parseErrors.length > 0) {
        const details = parseErrors.map(e => `Row ${e.row}: "${e.value}"`).join(', ');
        setDraftToast({
          message: t('multiTrade.paste.parseErrors', { count: parseErrors.length, details }),
          type: 'error',
        });
        return;
      }

      const tradeRows = trades.filter(r => r.rowType !== 'split');
      const startTradeIdx = tradeRows.findIndex(r => r.rowIndex === rowIndex);
      if (startTradeIdx === -1) return;
      const remainingRows = tradeRows.slice(startTradeIdx);

      if (parsedRows.length > remainingRows.length) {
        setDraftToast({
          message: t('multiTrade.paste.tooManyRows', { clipboard: parsedRows.length, grid: remainingRows.length }),
          type: 'error',
        });
        return;
      }

      // Apply via handleCellChange — existing maturity logic handles parseMaturity,
      // maturityDays, and maturityDate calculation automatically.
      const targetRows = remainingRows.slice(0, parsedRows.length);
      let applied = 0;
      for (let r = 0; r < parsedDays.length; r++) {
        const days = parsedDays[r];
        if (days != null) {
          handleCellChange(targetRows[r].rowIndex, 'maturity', `${days}D`);
          applied++;
        }
      }
      if (applied > 0) {
        setDraftToast({ message: t('multiTrade.paste.success', { count: applied }), type: 'success' });
      }
      return;
    }

    // --- Text fields (hedgeObject, notes): paste raw strings, no numeric parsing ---
    const TEXT_PASTE_FIELDS = ['hedgeObject', 'notes'];
    if (TEXT_PASTE_FIELDS.includes(field)) {
      const tradeRows = trades.filter(r => r.rowType !== 'split');
      const startTradeIdx = tradeRows.findIndex(r => r.rowIndex === rowIndex);
      if (startTradeIdx === -1) return;
      const remainingRows = tradeRows.slice(startTradeIdx);

      if (parsedRows.length > remainingRows.length) {
        setDraftToast({
          message: t('multiTrade.paste.tooManyRows', { clipboard: parsedRows.length, grid: remainingRows.length }),
          type: 'error',
        });
        return;
      }

      const targetRows = remainingRows.slice(0, parsedRows.length);
      let applied = 0;
      for (let r = 0; r < parsedRows.length; r++) {
        const val = parsedRows[r][0] ?? '';
        handleCellChange(targetRows[r].rowIndex, field, val);
        applied++;
      }
      if (applied > 0) {
        setDraftToast({ message: t('multiTrade.paste.success', { count: applied }), type: 'success' });
      }
      return;
    }

    // --- Determine target fields based on what was clicked ---
    let targetFields: string[];

    if (field === 'amount') {
      // Amount: single column only, ignore extra pasted columns
      targetFields = ['amount'];
    } else if (field.startsWith('alloc_')) {
      // Allocation: fill rightward through available alloc columns
      const startAllocIdx = parseInt(field.replace('alloc_', ''), 10);
      const allocCount = allocationColumns.length;
      targetFields = [];
      for (let i = startAllocIdx; i < allocCount && targetFields.length < colCount; i++) {
        targetFields.push(`alloc_${i}`);
      }
    } else {
      // Price fields: fill rightward through spotPrice → forwardPoints → price
      const startIdx = PRICE_FIELD_ORDER.indexOf(field);
      if (startIdx === -1) return;
      const availableFields = PRICE_FIELD_ORDER.slice(startIdx);
      targetFields = availableFields.slice(0, colCount);
    }

    if (targetFields.length === 0) return;

    // Determine target trade rows, starting from the clicked row.
    // Amount paste includes split children (amount is editable on split rows).
    // All other fields skip split children (they are read-only on splits).
    const tradeRows = field === 'amount' ? trades : trades.filter(t => t.rowType !== 'split');
    const startTradeIdx = tradeRows.findIndex(t => t.rowIndex === rowIndex);
    if (startTradeIdx === -1) return;
    const remainingRows = tradeRows.slice(startTradeIdx);

    // Validate row count
    if (parsedRows.length > remainingRows.length) {
      setDraftToast({
        message: t('multiTrade.paste.tooManyRows', { clipboard: parsedRows.length, grid: remainingRows.length }),
        type: 'error',
      });
      return;
    }

    // Parse all values, collecting details of any failures
    const parseErrors: { row: number; value: string }[] = [];
    const parsedValues: (number | null)[][] = parsedRows.map((cols, rowIdx) => {
      return targetFields.map((_, colIdx) => {
        const raw = cols[colIdx] || '';
        if (raw === '') return null;
        const parsed = parseAmountInput(raw);
        if (parsed === null) {
          parseErrors.push({ row: rowIdx + 1, value: raw });
        }
        return parsed;
      });
    });

    if (parseErrors.length > 0) {
      const details = parseErrors.map(e => `Row ${e.row}: "${e.value}"`).join(', ');
      setDraftToast({
        message: t('multiTrade.paste.parseErrors', { count: parseErrors.length, details }),
        type: 'error',
      });
      return;
    }

    const targetRowIndexes = remainingRows.slice(0, parsedRows.length).map(r => r.rowIndex);

    // Validate price triangulation when all 3 price fields are being pasted
    const mismatchRows: number[] = [];
    if (targetFields.length === 3 && targetFields[0] === 'spotPrice') {
      for (let r = 0; r < parsedValues.length; r++) {
        const [spot, fwdPts, price] = parsedValues[r];
        if (spot != null && fwdPts != null && price != null) {
          const expected = spot + fwdPts;
          if (Math.abs(expected - price) > 0.015) {
            mismatchRows.push(r);
          }
        }
      }
    }

    // Check for existing values that would be overwritten
    let overwriteCount = 0;
    const isAllocPaste = targetFields[0].startsWith('alloc_');
    for (let r = 0; r < parsedValues.length; r++) {
      const rIdx = targetRowIndexes[r];
      if (isAllocPaste) {
        // Check allocation data
        const tradeAlloc = allocations.find(a => a.tradeRowIndex === rIdx);
        if (tradeAlloc) {
          for (const f of targetFields) {
            const allocIdx = parseInt(f.replace('alloc_', ''), 10);
            const existing = tradeAlloc.allocations[allocIdx]?.amount;
            if (existing != null && existing > 0) {
              overwriteCount++;
              break;
            }
          }
        }
      } else {
        const row = trades.find(t => t.rowIndex === rIdx);
        if (!row) continue;
        for (const f of targetFields) {
          const existing = (row as any)[f];
          if (existing != null && existing !== 0) {
            overwriteCount++;
            break;
          }
        }
      }
    }

    if (overwriteCount > 0) {
      setPendingPaste({ fields: targetFields, values: parsedValues, targetRowIndexes, mismatchRows });
      return;
    }

    // No overwrite needed — apply immediately
    applyPasteValues(targetFields, parsedValues, targetRowIndexes, mismatchRows);
  }, [trades, allocations, allocationColumns, t, applyPasteValues]);

  // --- Copy/duplicate a row ---
  const handleCopyRow = useCallback((rowIndex: number, e: React.MouseEvent) => {
    // If the row is a parent (has splits), show the inline prompt near the button
    const source = trades.find(t => t.rowIndex === rowIndex);
    if (source?.rowType === 'parent') {
      setPendingCopy({ rowIndex, x: e.clientX, y: e.clientY });
      return;
    }
    copyRowAsStandalone(rowIndex);
  }, [trades]);

  // Helper: copy a single row as a standalone trade (no splits)
  const copyRowAsStandalone = useCallback((rowIndex: number) => {
    setPendingCopy(null);
    setTrades(prev => {
      const source = prev.find(t => t.rowIndex === rowIndex);
      if (!source) return prev;
      const maxIndex = Math.max(...prev.map(r => r.rowIndex));
      const copy: MultiTradeRow = {
        ...source,
        rowIndex: maxIndex + 1,
        selected: true,
        confidence: {},
        priceManuallyEdited: false,
        validationErrors: validateRow(source),
        rowType: 'trade',
        groupId: undefined,
        splitPercentage: undefined,
      };
      // Copy allocations for the new row
      if (allocationColumns.length > 0) {
        const sourceAlloc = allocations.find(a => a.tradeRowIndex === source.rowIndex);
        if (sourceAlloc) {
          setAllocations(prevAllocs => [...prevAllocs, { tradeRowIndex: copy.rowIndex, allocations: sourceAlloc.allocations.map(a => ({ ...a })) }]);
        } else {
          const emptyEntries: AllocationEntry[] = allocationColumns.map(c => ({
            entityName: c.entityName, grouping1: c.grouping1, grouping2: c.grouping2, grouping3: c.grouping3,
            hedgeObject: c.hedgeObject, notes: c.notes,
          }));
          setAllocations(prevAllocs => [...prevAllocs, { tradeRowIndex: copy.rowIndex, allocations: emptyEntries }]);
        }
      }
      return [...prev, copy];
    });
  }, [allocationColumns, allocations]);

  // Helper: copy a parent row together with all its split children
  const copyRowWithSplits = useCallback((rowIndex: number) => {
    setPendingCopy(null);
    setTrades(prev => {
      const source = prev.find(t => t.rowIndex === rowIndex);
      if (!source || !source.groupId) return prev;
      const splits = prev.filter(t => t.rowType === 'split' && t.groupId === source.groupId);
      const maxIndex = Math.max(...prev.map(r => r.rowIndex));
      const newGroupId = `grp_${Date.now()}_${maxIndex + 1}`;
      // Copy parent
      const parentCopy: MultiTradeRow = {
        ...source,
        rowIndex: maxIndex + 1,
        selected: true,
        confidence: {},
        priceManuallyEdited: false,
        validationErrors: validateRow(source),
        groupId: newGroupId,
      };
      // Copy each split child
      const splitCopies: MultiTradeRow[] = splits.map((s, i) => ({
        ...s,
        rowIndex: maxIndex + 2 + i,
        confidence: {},
        validationErrors: validateRow(s),
        groupId: newGroupId,
      }));
      // Copy allocations for all new rows
      if (allocationColumns.length > 0) {
        const allNewRows = [parentCopy, ...splitCopies];
        const allSourceRows = [source, ...splits];
        const newAllocs: TradeAllocationSet[] = [];
        allNewRows.forEach((newRow, i) => {
          const srcRow = allSourceRows[i];
          const sourceAlloc = allocations.find(a => a.tradeRowIndex === srcRow.rowIndex);
          if (sourceAlloc) {
            newAllocs.push({ tradeRowIndex: newRow.rowIndex, allocations: sourceAlloc.allocations.map(a => ({ ...a })) });
          } else {
            const emptyEntries: AllocationEntry[] = allocationColumns.map(c => ({
              entityName: c.entityName, grouping1: c.grouping1, grouping2: c.grouping2, grouping3: c.grouping3,
              hedgeObject: c.hedgeObject, notes: c.notes,
            }));
            newAllocs.push({ tradeRowIndex: newRow.rowIndex, allocations: emptyEntries });
          }
        });
        setAllocations(prevAllocs => [...prevAllocs, ...newAllocs]);
      }
      return [...prev, parentCopy, ...splitCopies];
    });
  }, [allocationColumns, allocations]);

  // --- Delete a single row (also cleans up split groups) ---
  const handleDeleteRow = useCallback((rowIndex: number) => {
    setTrades(prev => {
      const row = prev.find(t => t.rowIndex === rowIndex);
      if (!row) return prev;
      // If deleting a split child, just remove it. If group has < 2 splits left, unsplit the parent.
      if (row.rowType === 'split' && row.groupId) {
        const remaining = prev.filter(t => t.rowIndex !== rowIndex);
        const siblingSplits = remaining.filter(t => t.rowType === 'split' && t.groupId === row.groupId);
        if (siblingSplits.length < 2) {
          // Unsplit: revert parent to trade, remove remaining split
          return remaining.map(t => {
            if (t.groupId === row.groupId && t.rowType === 'parent') {
              return { ...t, rowType: 'trade' as const, groupId: undefined };
            }
            return t;
          }).filter(t => !(t.rowType === 'split' && t.groupId === row.groupId));
        }
        return remaining;
      }
      // If deleting a parent, remove the entire group
      if (row.rowType === 'parent' && row.groupId) {
        return prev.filter(t => t.groupId !== row.groupId);
      }
      return prev.filter(t => t.rowIndex !== rowIndex);
    });
  }, []);

  // --- Split a trade row: convert to parent + create 2 empty split children ---
  const handleSplitRow = useCallback((rowIndex: number) => {
    setTrades(prev => {
      const row = prev.find(t => t.rowIndex === rowIndex);
      if (!row || row.rowType !== 'trade') return prev;
      const groupId = `grp_${Date.now()}_${rowIndex}`;
      const maxIndex = Math.max(...prev.map(r => r.rowIndex));
      const parent: MultiTradeRow = { ...row, rowType: 'parent', groupId };
      const split1: MultiTradeRow = {
        ...createEmptyRow(maxIndex + 1),
        rowType: 'split',
        groupId,
        isForwardStarting: row.isForwardStarting,
        amount: null,
        splitPercentage: undefined,
        grouping1: '',
        grouping2: '',
        grouping3: '',
        hedgeObject: '',
        notes: '',
      };
      const split2: MultiTradeRow = {
        ...createEmptyRow(maxIndex + 2),
        rowType: 'split',
        groupId,
        isForwardStarting: row.isForwardStarting,
        amount: null,
        splitPercentage: undefined,
        grouping1: '',
        grouping2: '',
        grouping3: '',
        hedgeObject: '',
        notes: '',
      };
      const idx = prev.findIndex(t => t.rowIndex === rowIndex);
      const next = [...prev];
      next.splice(idx, 1, parent, split1, split2);
      return next;
    });
  }, []);

  // --- Add another split child to a group ---
  const handleAddSplitChild = useCallback((groupId: string) => {
    setTrades(prev => {
      const maxIndex = Math.max(...prev.map(r => r.rowIndex));
      const parentRow = prev.find(t => t.groupId === groupId && t.rowType === 'parent');
      const newSplit: MultiTradeRow = {
        ...createEmptyRow(maxIndex + 1),
        rowType: 'split',
        groupId,
        isForwardStarting: parentRow?.isForwardStarting ?? false,
        amount: null,
        splitPercentage: undefined,
        grouping1: '',
        grouping2: '',
        grouping3: '',
        hedgeObject: '',
        notes: '',
      };
      // Insert after last split in this group
      const lastSplitIdx = prev.reduce((last, t, i) => (t.groupId === groupId && t.rowType === 'split') ? i : last, -1);
      if (lastSplitIdx === -1) return prev;
      const next = [...prev];
      next.splice(lastSplitIdx + 1, 0, newSplit);
      return next;
    });
  }, []);

  // --- Unsplit: revert parent to trade, remove all split children ---
  const handleUnsplitRow = useCallback((rowIndex: number) => {
    setTrades(prev => {
      const row = prev.find(t => t.rowIndex === rowIndex);
      if (!row || row.rowType !== 'parent' || !row.groupId) return prev;
      const groupId = row.groupId;
      return prev
        .filter(t => !(t.rowType === 'split' && t.groupId === groupId))
        .map(t => t.rowIndex === rowIndex ? { ...t, rowType: 'trade' as const, groupId: undefined } : t);
    });
  }, []);

  // --- Remove selected ---
  const handleRemoveSelected = useCallback(() => {
    setTrades(prev => prev.filter(t => !t.selected));
  }, []);

  // --- Resolve party IDs for submission ---
  const resolvePartyIds = useCallback((formData: TradeBookingForm): TradeBookingForm => {
    const bank = banks.find(b => b.name.toLowerCase() === formData.counterparty.toLowerCase());
    if (bank) {
      formData.counterpartyId = bank.id || '';
      formData.counterpartyTaxId = bank.taxId || '';
    }
    const entity = tradingEntities.find(e => e.name.toLowerCase() === formData.ourParty.toLowerCase());
    if (entity) {
      formData.ourPartyId = entity.id || '';
      formData.ourPartyTaxId = entity.taxId || '';
    }
    return formData;
  }, [banks, tradingEntities]);

  // (Allocation paste panel removed — allocations are now entered directly in table columns)

  // --- Resolve allocation entity IDs for submission ---
  const resolveAllocationsForTrade = useCallback((tradeRowIndex: number, tradeAmount: number): AllocationRow[] | undefined => {
    const tradeAlloc = allocations.find(a => a.tradeRowIndex === tradeRowIndex);
    if (!tradeAlloc || tradeAlloc.allocations.length === 0) return undefined;

    // Filter out empty allocation entries (no amount set)
    const nonEmpty = tradeAlloc.allocations.filter(a => a.amount && a.amount > 0);
    if (nonEmpty.length === 0) return undefined;

    return nonEmpty.map(a => {
      const entity = tradingEntities.find(e => e.name.toLowerCase() === (a.entityName || '').toLowerCase());
      const amount = a.amount || 0;
      const percentage = a.percentage || (tradeAmount > 0 ? Math.round((amount / tradeAmount) * 10000) / 100 : 0);
      return {
        entityId: entity?.id || '',
        entityName: a.entityName || '',
        entityTaxId: entity?.taxId || '',
        grouping1: a.grouping1 || '',
        grouping2: a.grouping2 || '',
        grouping3: a.grouping3 || '',
        amount,
        percentage,
        hedgeObject: a.hedgeObject || '',
        notes: a.notes || '',
      };
    });
  }, [allocations, tradingEntities]);

  // --- Book All handler ---
  const handleBookAll = useCallback(async () => {
    setShowValidation(true);

    // If any split groups exist, use per-group submission logic
    const hasSplitGroups = trades.some(t => t.rowType === 'parent' || t.rowType === 'split');
    if (hasSplitGroups) {
      // Collect groups (parent + children) and standalone trade rows
      type SubmitUnit = { type: 'group'; parent: MultiTradeRow; splits: MultiTradeRow[] } | { type: 'trade'; row: MultiTradeRow };
      const units: SubmitUnit[] = [];
      const processedGroupIds = new Set<string>();

      for (const row of trades) {
        if (!row.selected) continue;
        if (row.rowType === 'split') continue; // handled via parent group
        if (row.rowType === 'parent' && row.groupId) {
          if (processedGroupIds.has(row.groupId)) continue;
          processedGroupIds.add(row.groupId);
          const children = trades.filter(t => t.groupId === row.groupId && t.rowType === 'split');
          units.push({ type: 'group', parent: row, splits: children });
        } else if (row.rowType === 'trade' && isRowReadyForValidation(row)) {
          units.push({ type: 'trade', row });
        }
      }

      // Validate all units before submitting
      const fmtNum = (n: number) => n.toLocaleString(i18n.language);
      const validationErrors: string[] = [];
      for (const unit of units) {
        if (unit.type === 'group') {
          const { parent, splits } = unit;
          if (!isRowReadyForValidation(parent)) continue;
          if (parent.validationErrors.length > 0) {
            validationErrors.push(t('multiTrade.splits.validationErrors'));
          }
          if (splits.length < 2) {
            validationErrors.push(t('multiTrade.splits.needAtLeast2'));
          }
          const splitTotal = splits.reduce((sum, s) => sum + (s.amount || 0), 0);
          if (parent.amount && Math.abs(splitTotal - parent.amount) > 1) {
            validationErrors.push(t('multiTrade.splits.amountsMismatch', {
              splitTotal: fmtNum(splitTotal),
              parentAmount: fmtNum(parent.amount),
            }));
          }
          if (splits.some(s => !s.amount || s.amount <= 0)) {
            validationErrors.push(t('multiTrade.splits.positiveAmounts'));
          }
        } else {
          if (unit.row.validationErrors.length > 0) {
            validationErrors.push(t('multiTrade.splits.tradeHasErrors'));
          }
        }
      }
      if (units.length === 0) return;
      if (validationErrors.length > 0) {
        setSubmitAlert({
          title: t('multiTrade.submitFailed.title'),
          message: validationErrors.join('\n'),
        });
        return;
      }

      setIsSubmitting(true);
      setSubmitProgress({ current: 0, total: units.length, type: 'booking' });

      let successCount = 0;
      let failCount = 0;
      const failedIndices = new Set<number>();
      const errorMessages: string[] = [];
      const collectedMatches: Array<{ tradeNumber: string; match: PotentialConfirmationMatch; bookingEntityId: string; status: 'pending' | 'processing' | 'success' | 'error' }> = [];

      for (let i = 0; i < units.length; i++) {
        setSubmitProgress(prev => ({ ...prev, current: i + 1 }));
        const unit = units[i];
        try {
          if (unit.type === 'group') {
            const formData = resolvePartyIds(rowToTradeBookingForm(unit.parent));
            const splitRows: SplitRow[] = unit.splits.map(s => ({
              grouping1: s.grouping1 || '',
              grouping2: s.grouping2 || '',
              grouping3: s.grouping3 || '',
              amount: s.amount || 0,
              percentage: s.splitPercentage || 0,
              hedgeObject: s.hedgeObject || '',
              notes: s.notes || '',
            }));
            const entityId = formData.ourPartyId || clientId;
            const result = await submitTrade(formData, entityId, undefined, undefined, splitRows);
            successCount++;
            // KAN-216: Collect confirmation match if present
            if (result.potentialConfirmationMatch) {
              collectedMatches.push({ tradeNumber: result.tradeNumber, match: result.potentialConfirmationMatch, bookingEntityId: entityId, status: 'pending' });
            }
          } else {
            const formData = resolvePartyIds(rowToTradeBookingForm(unit.row));
            const entityId = formData.ourPartyId || clientId;
            const result = await submitTrade(formData, entityId);
            successCount++;
            // KAN-216: Collect confirmation match if present
            if (result.potentialConfirmationMatch) {
              collectedMatches.push({ tradeNumber: result.tradeNumber, match: result.potentialConfirmationMatch, bookingEntityId: entityId, status: 'pending' });
            }
          }
        } catch (err: any) {
          failCount++;
          if (unit.type === 'group') {
            failedIndices.add(unit.parent.rowIndex);
            unit.splits.forEach(s => failedIndices.add(s.rowIndex));
          } else {
            failedIndices.add(unit.row.rowIndex);
          }
          errorMessages.push(`Item ${i + 1}: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
        }
      }

      setIsSubmitting(false);

      if (failCount === 0) {
        setSubmitResults({ success: successCount, failed: 0 });
        // Delete draft if all rows booked successfully
        if (activeDraftId) {
          try {
            await deleteMultiTradeDraft(clientId, activeDraftId);
          } catch (err) {
            console.warn('KAN-242: Failed to auto-delete draft after submission:', activeDraftId, err);
          }
        }
        // KAN-216: If matches found, show them instead of auto-closing
        if (collectedMatches.length > 0) {
          setConfirmationMatches(collectedMatches);
        } else {
          setTimeout(() => onClose(), 1500);
        }
      } else {
        setSubmitResults({ success: successCount, failed: failCount });
        setSubmitAlert({
          title: t('multiTrade.submitFailed.title'),
          message: t('multiTrade.submitFailed.message', { failed: failCount, total: units.length })
            + '\n\n' + errorMessages.join('\n'),
        });
        setTrades(prev => prev.filter(t => failedIndices.has(t.rowIndex)));
        // KAN-216: Still show matches even with partial failures
        if (collectedMatches.length > 0) {
          setConfirmationMatches(collectedMatches);
        }
      }
      return;
    }

    // Normal / Allocations mode: submit each selected row independently
    const selected = trades.filter(t => t.selected && isRowReadyForValidation(t));
    const hasErrors = selected.some(t => t.validationErrors.length > 0);
    if (hasErrors || selected.length === 0) return;

    setIsSubmitting(true);
    setSubmitProgress({ current: 0, total: selected.length, type: 'booking' });

    let successCount = 0;
    let failCount = 0;
    const failedIndices = new Set<number>();
    const errorMessages: string[] = [];
    const collectedMatches: Array<{ tradeNumber: string; match: PotentialConfirmationMatch; bookingEntityId: string; status: 'pending' | 'processing' | 'success' | 'error' }> = [];

    for (let i = 0; i < selected.length; i++) {
      setSubmitProgress(prev => ({ ...prev, current: i + 1 }));
      try {
        const formData = resolvePartyIds(rowToTradeBookingForm(selected[i]));
        const tradeAllocations = resolveAllocationsForTrade(selected[i].rowIndex, selected[i].amount || 0);
        const entityId = formData.ourPartyId || clientId;
        const result = await submitTrade(formData, entityId, undefined, tradeAllocations);
        successCount++;
        // KAN-216: Collect confirmation match if present
        if (result.potentialConfirmationMatch) {
          collectedMatches.push({ tradeNumber: result.tradeNumber, match: result.potentialConfirmationMatch, bookingEntityId: entityId, status: 'pending' });
        }
      } catch (err: any) {
        failCount++;
        failedIndices.add(selected[i].rowIndex);
        errorMessages.push(`Trade ${i + 1}: ${err?.response?.data?.detail || err?.message || 'Unknown error'}`);
      }
    }

    setIsSubmitting(false);

    if (failCount === 0) {
      setSubmitResults({ success: successCount, failed: 0 });
      // Delete draft if all rows booked successfully
      if (activeDraftId) {
        try {
          await deleteMultiTradeDraft(clientId, activeDraftId);
        } catch (err) {
          console.warn('KAN-242: Failed to auto-delete draft after submission:', activeDraftId, err);
        }
      }
      // KAN-216: If matches found, show them instead of auto-closing
      if (collectedMatches.length > 0) {
        setConfirmationMatches(collectedMatches);
      } else {
        setTimeout(() => onClose(), 1500);
      }
    } else {
      setSubmitResults({ success: successCount, failed: failCount });
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: t('multiTrade.submitFailed.message', { failed: failCount, total: selected.length })
          + '\n\n' + errorMessages.join('\n'),
      });
      // Keep only failed rows
      setTrades(prev => prev.filter(t => failedIndices.has(t.rowIndex)));
      // KAN-216: Still show matches even with partial failures
      if (collectedMatches.length > 0) {
        setConfirmationMatches(collectedMatches);
      }
    }
  }, [trades, gridMode, clientId, activeDraftId, resolvePartyIds, resolveAllocationsForTrade, onClose, t]);

  // KAN-216: Reprocess a single confirmation match
  const handleReprocessMatch = useCallback(async (index: number) => {
    const entry = confirmationMatches[index];
    if (!entry || entry.match.type !== 'single' || !entry.match.emailId) return;

    setConfirmationMatches(prev => prev.map((m, i) => i === index ? { ...m, status: 'processing' as const } : m));
    try {
      const result = await mainClientService.reprocessEmail(entry.bookingEntityId, entry.match.emailId, entry.match.tradeIndex);
      const newStatus = result.success ? 'success' as const : 'error' as const;
      setConfirmationMatches(prev => prev.map((m, i) => i === index ? { ...m, status: newStatus } : m));

      // KAN-260: Dismiss companion provisionals after successful definitive match
      if (result.success && entry.match.companionProvisionals?.length) {
        for (const provisional of entry.match.companionProvisionals) {
          try {
            await mainClientService.dismissEmail(
              entry.bookingEntityId,
              provisional.emailId,
              provisional.tradeIndex,
              provisional.bankTradeNumber,
            );
          } catch (dismissErr) {
            // Non-fatal: provisional stays in the tray but doesn't cause harm
            console.warn('KAN-260: Failed to dismiss companion provisional:', provisional.bankTradeNumber, dismissErr);
          }
        }
      }
    } catch {
      setConfirmationMatches(prev => prev.map((m, i) => i === index ? { ...m, status: 'error' as const } : m));
    }
  }, [confirmationMatches]);

  // KAN-216: Reprocess all unambiguous (single-type) matches
  const handleReprocessAll = useCallback(async () => {
    const singleIndices = confirmationMatches
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.match.type === 'single' && m.status === 'pending')
      .map(({ i }) => i);

    for (const idx of singleIndices) {
      await handleReprocessMatch(idx);
    }
  }, [confirmationMatches, handleReprocessMatch]);

  // KAN-216: Skip all matches and close
  const handleSkipAllMatches = useCallback(() => {
    setConfirmationMatches([]);
    onClose();
  }, [onClose]);

  // --- Save Draft handler (saves entire modal state as a single document) ---
  const serializeDraftPayload = useCallback(() => {
    // Strip transient UI fields from rows before saving
    const cleanTrades = trades.map(({ selected, validationErrors, confidence, ...rest }) => rest);
    return {
      name: activeDraftName,
      mode: gridMode,
      trades: cleanTrades,
      allocationColumns: allocationColumns,
      allocations: allocations,
    };
  }, [trades, gridMode, allocationColumns, allocations, activeDraftName]);

  const handleSaveDraft = useCallback(async () => {
    if (trades.length === 0) return;

    // First save — prompt for name
    if (!activeDraftId) {
      setDraftNameInput('');
      setShowDraftNamePrompt(true);
      return;
    }

    // Subsequent saves — update in place
    setDraftSaving(true);
    try {
      const payload = serializeDraftPayload();
      await updateMultiTradeDraft(clientId, activeDraftId, payload);
      setDraftToast({ message: t('multiTrade.draftSaved'), type: 'success' });
    } catch (err: any) {
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: err?.response?.data?.detail || err?.message || 'Failed to save draft',
      });
    } finally {
      setDraftSaving(false);
    }
  }, [trades, activeDraftId, clientId, serializeDraftPayload, t]);

  const handleCreateDraft = useCallback(async () => {
    const name = draftNameInput.trim();
    if (!name) return;

    setDraftSaving(true);
    setShowDraftNamePrompt(false);
    try {
      const cleanTrades = trades.map(({ selected, validationErrors, confidence, ...rest }) => rest);
      const payload = { name, mode: gridMode, trades: cleanTrades, allocationColumns, allocations };
      const result = await createMultiTradeDraft(clientId, payload);
      setActiveDraftId(result.id);
      activeDraftIdRef.current = result.id;
      setActiveDraftName(name);
      setDraftToast({ message: t('multiTrade.draftSaved'), type: 'success' });
    } catch (err: any) {
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: err?.response?.data?.detail || err?.message || 'Failed to create draft',
      });
    } finally {
      setDraftSaving(false);
    }
  }, [draftNameInput, trades, gridMode, allocationColumns, allocations, clientId, t]);

  // --- Load Draft handler ---
  const handleOpenDraftPicker = useCallback(async () => {
    try {
      const result = await listMultiTradeDrafts(clientId);
      setDrafts(result || []);
      setShowDraftPicker(true);
    } catch (err: any) {
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: err?.response?.data?.detail || err?.message || 'Failed to load drafts',
      });
    }
  }, [clientId, t]);

  const [pendingDraftLoad, setPendingDraftLoad] = useState<string | null>(null);

  const handleLoadDraft = useCallback(async (draftId: string) => {
    // Check for unsaved data
    const hasData = trades.some(r => r.amount || r.counterparty || r.price);
    if (hasData && !activeDraftId) {
      setShowDraftPicker(false);
      setPendingDraftLoad(draftId);
      return;
    }
    await executeLoadDraft(draftId);
  }, [trades, activeDraftId]);

  const executeLoadDraft = useCallback(async (draftId: string) => {
    try {
      const draft = await getMultiTradeDraft(clientId, draftId);

      // Attempt to acquire the draft lock before applying any state changes.
      let lockResult: { lockedByName: string; lockedUntil: string } | null = null;
      try {
        lockResult = await lockMultiTradeDraft(clientId, draftId);
      } catch {
        // Non-423 failure (network error etc.) — fail open with a warning.
        setDraftToast({ message: t('multiTrade.draftLockFailed'), type: 'warning' });
      }

      if (lockResult) {
        // 423 — draft is locked by another user; abort load.
        setDraftToast({
          message: t('multiTrade.draftLocked', { name: lockResult.lockedByName }),
          type: 'error',
        });
        return;
      }

      // Restore state
      setGridMode((draft.mode === 'splits' ? 'normal' : draft.mode) as GridMode);
      setTrades(draft.trades.map((r: any, i: number) => ({
        ...r,
        rowIndex: r.rowIndex ?? i,
        selected: true,
        validationErrors: [],
        confidence: r.confidence || {},
      })));
      setAllocationColumns(draft.allocationColumns || []);
      setAllocations(draft.allocations || []);
      setActiveDraftId(draftId);
      activeDraftIdRef.current = draftId;
      setActiveDraftName(draft.name);
      setShowDraftPicker(false);
      setDraftToast({ message: t('multiTrade.draftLoaded'), type: 'info' });
    } catch (err: any) {
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: err?.response?.data?.detail || err?.message || 'Failed to load draft',
      });
    }
  }, [clientId, t]);

  // --- Auto-load initialDraftId on mount (e.g. opened via right-click from TradeBooking) ---
  useEffect(() => {
    if (initialDraftId) {
      executeLoadDraft(initialDraftId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount only

  // --- Draft lock heartbeat: refresh the lock every 5 minutes while a draft is open ---
  useEffect(() => {
    if (!activeDraftId) return;
    const interval = setInterval(async () => {
      try {
        const result = await lockMultiTradeDraft(clientId, activeDraftId);
        if (result) {
          // Another user has taken the lock (our lock expired)
          setDraftToast({ message: t('multiTrade.draftLockExpired'), type: 'warning' });
        }
      } catch {
        // Swallow — heartbeat failures are non-critical
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [activeDraftId, clientId, t]);

  // --- Draft lock release on modal unmount ---
  useEffect(() => {
    return () => {
      if (activeDraftIdRef.current) {
        unlockMultiTradeDraft(clientId, activeDraftIdRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]); // clientId is stable; activeDraftIdRef is a ref (not state)

  // --- Delete Draft handler ---
  const [pendingDraftDelete, setPendingDraftDelete] = useState<string | null>(null);

  const handleDeleteDraft = useCallback(async (draftId: string) => {
    try {
      await deleteMultiTradeDraft(clientId, draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      if (activeDraftId === draftId) {
        setActiveDraftId(null);
        activeDraftIdRef.current = null;
        setActiveDraftName('');
      }
      setDraftToast({ message: t('multiTrade.draftDeleted'), type: 'warning' });
      setShowDraftPicker(true);
    } catch (err: any) {
      setSubmitAlert({
        title: t('multiTrade.submitFailed.title'),
        message: err?.response?.data?.detail || err?.message || 'Failed to delete draft',
      });
    }
    setPendingDraftDelete(null);
  }, [clientId, activeDraftId, t]);

  // --- Clear Form handler ---
  const handleClearForm = useCallback(() => {
    setTrades([createEmptyRow(0, systemDate || undefined, columnDefaults)]);
    setAllocations([]);
    setAllocationColumns([]);
    setGridMode('normal');
    setActiveDraftId(null);
    activeDraftIdRef.current = null;
    setActiveDraftName('');
  }, [systemDate, columnDefaults]);

  // --- Render ---
  return (
    <div className="multi-trade-overlay">
      <div className="multi-trade-modal">
        {/* Header */}
        <div className="multi-trade-header">
          <div className="multi-trade-header-left">
            <h2>{extractionResult ? t('multiTrade.title') : t('multiTrade.manualEntryTitle')}</h2>
            {extractionResult && (
              <span className="multi-trade-source">
                {t('multiTrade.source', {
                  name: extractionResult.sourceTypeName,
                  count: extractionResult.tradesExtracted,
                })}
                {isForwardStartingExtraction && (
                  <> &middot; {t('multiTrade.forwardStartingLabel')}</>
                )}
              </span>
            )}
            {showValidation && errorCount > 0 && (
              <span className="multi-trade-validation-warning">
                {t('multiTrade.validationSummary', { count: errorCount })}
              </span>
            )}
            {extractionResult && extractionResult.tradesExtracted >= extractionResult.maxTradesAllowed && (
              <span className="multi-trade-max-warning">
                {t('multiTrade.moreRowsWarning', { max: extractionResult.maxTradesAllowed })}
              </span>
            )}
          </div>
          {/* Mode selector */}
          <div className="multi-trade-mode-selector">
            <button
              className={`multi-trade-mode-btn${gridMode === 'normal' ? ' active' : ''}`}
              onClick={() => handleModeChange('normal')}
              disabled={isSubmitting}
            >
              {t('multiTrade.modes.normal')}
            </button>
            <button
              className={`multi-trade-mode-btn${gridMode === 'allocations' ? ' active' : ''}`}
              onClick={() => handleModeChange('allocations')}
              disabled={isSubmitting}
            >
              {t('multiTrade.modes.allocations')}
            </button>
          </div>
          {/* Draft controls */}
          <div className="multi-trade-draft-controls">
            <button
              className="multi-trade-draft-load-btn"
              onClick={handleOpenDraftPicker}
              disabled={isSubmitting}
            >
              {t('multiTrade.loadDraft')}
            </button>
            <button
              className="multi-trade-clear-btn"
              onClick={handleClearForm}
              disabled={isSubmitting}
            >
              {t('multiTrade.clearForm')}
            </button>
            {activeDraftName && (
              <span className="multi-trade-draft-indicator" title={activeDraftName}>
                {t('multiTrade.draftIndicator', { name: activeDraftName })}
              </span>
            )}
          </div>
          <button
            className="multi-trade-mailto-btn"
            title={t('multiTrade.downloadExcel')}
            onClick={handleExportToExcel}
            disabled={trades.length === 0}
          >
            <FileSpreadsheet size={16} />
          </button>
          <div style={{ position: 'relative' }}>
            <button
              className="multi-trade-mailto-btn"
              title={t('multiTrade.sendEmail')}
              onClick={async () => {
                const mailtoRows: MultiTradeMailtoRow[] = trades.filter(r => r.rowType !== 'split').map(r => ({
                  tradeType: r.tradeType,
                  ourParty: r.ourParty,
                  direction: r.direction,
                  amount: r.amount,
                  currency1: r.currency1,
                  currency2: r.currency2,
                  spotPrice: r.spotPrice,
                  forwardPoints: r.forwardPoints,
                  price: r.price,
                  contraAmount: r.contraAmount,
                  tradeDate: r.tradeDate,
                  maturityDate: r.maturityDate,
                  settlementType: r.settlementType,
                  settlementCurrency: r.settlementCurrency,
                  fixingReference: r.fixingReference,
                  isForwardStarting: r.isForwardStarting,
                  selected: r.selected,
                }));
                const html = generateMultiTradeTableHtml(mailtoRows, i18n.language);
                if (!html) return;
                // Copy HTML table to clipboard
                try {
                  const blob = new Blob([html], { type: 'text/html' });
                  await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
                } catch {
                  // Fallback: copy as plain text
                  await navigator.clipboard.writeText(html);
                }
                // Open mailto with subject + greeting shell + saved BCC recipients
                const mailto = getMultiTradeMailtoUrl(mailtoRows, i18n.language, mailtoRecipients);
                if (mailto) window.location.href = mailto;
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setShowMailtoSettings(prev => !prev);
              }}
              disabled={!trades.some(r => r.selected && r.rowType !== 'split') && !showMailtoSettings}
            >
              <Mail size={16} />
            </button>
            {showMailtoSettings && (
              <div className="multi-trade-mailto-popover" ref={mailtoPopoverRef} onClick={(e) => e.stopPropagation()}>
                <div className="multi-trade-mailto-popover-title">{t('multiTrade.mailtoSettings.title')}</div>
                {mailtoRecipients.length === 0 && (
                  <div className="multi-trade-mailto-popover-empty">{t('multiTrade.mailtoSettings.empty')}</div>
                )}
                {mailtoRecipients.map(email => (
                  <div key={email} className="multi-trade-mailto-recipient">
                    <span className="multi-trade-mailto-recipient-email">{email}</span>
                    <button
                      className="multi-trade-mailto-recipient-remove"
                      onClick={() => handleRemoveMailtoRecipient(email)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="multi-trade-mailto-add-row">
                  <input
                    type="email"
                    className="multi-trade-mailto-add-input"
                    placeholder={t('multiTrade.mailtoSettings.placeholder')}
                    value={mailtoInput}
                    onChange={(e) => setMailtoInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddMailtoRecipient(); }}
                  />
                  <button
                    className="multi-trade-mailto-add-btn"
                    onClick={handleAddMailtoRecipient}
                    disabled={!mailtoInput.trim()}
                  >
                    {t('multiTrade.mailtoSettings.add')}
                  </button>
                </div>
              </div>
            )}
          </div>
          <button className="multi-trade-close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Body: Table + Allocation panel side by side (hidden when confirmation matches are showing) */}
        {confirmationMatches.length === 0 && <div className="multi-trade-body">
          <MultiTradeTable
            trades={trades}
            gridMode={gridMode}
            totalsRows={pinnedBottomRowData}
            allocations={allocations}
            allocationColumns={allocationColumns}
            isForwardStartingExtraction={isForwardStartingExtraction}
            groupingsConfig={groupingsConfig}
            tradingEntities={tradingEntities}
            counterpartyNames={counterpartyNames}
            ourPartyNames={ourPartyNames}
            showValidation={showValidation}
            columnDefaults={columnDefaults}
            onColumnDefaultChange={handleColumnDefaultChange}
            onCellChange={handleCellChange}
            onAllocCellChange={handleAllocCellChange}
            onAllocCopyDown={handleAllocCopyDown}
            onRemoveAllocColumn={handleRemoveAllocColumn}
            onAllocColumnUpdate={handleUpdateAllocColumn}
            onCopyFieldDown={handleCopyFieldDown}
            onCopyDateOffset={handleCopyDateOffset}
            onAddRow={handleAddRow}
            onSplitRow={handleSplitRow}
            onUnsplitRow={handleUnsplitRow}
            onAddSplitChild={handleAddSplitChild}
            onCopyRow={handleCopyRow}
            onDeleteRow={handleDeleteRow}
            onToggleSelect={toggleRowSelection}
            onToggleSelectAll={toggleSelectAll}
            onPasteValues={handlePasteValues}
            isSubmitting={isSubmitting}
          />

          {/* Allocation sidebar: toggle tab + entity/grouping selectors */}
          {gridMode === 'allocations' && (
            <div className={`multi-trade-alloc-sidebar ${allocSidebarOpen ? '' : 'collapsed'}`}>
              <button
                className="multi-trade-alloc-sidebar-toggle"
                onClick={() => setAllocSidebarOpen(prev => !prev)}
                title={allocSidebarOpen ? t('common.hide') : t('multiTrade.allocations.addColumn')}
              >
                <span className={`multi-trade-alloc-sidebar-chevron ${allocSidebarOpen ? '' : 'rotated'}`}>&#x276F;</span>
              </button>
              {allocSidebarOpen && (
                <div className="multi-trade-alloc-sidebar-content">
                  <h4>{t('multiTrade.allocations.addColumn')}</h4>
                  <div className="multi-trade-alloc-sidebar-form">
                    <label className="multi-trade-alloc-sidebar-label">{t('multiTrade.allocations.selectEntity')}</label>
                    <select
                      className="multi-trade-alloc-sidebar-select"
                      value={newAllocEntity}
                      onChange={(e) => { setNewAllocEntity(e.target.value); setNewAllocG1(''); setNewAllocG2(''); setNewAllocG3(''); }}
                    >
                      <option value="">—</option>
                      {ourPartyNames.map(name => <option key={name} value={name}>{name}</option>)}
                    </select>
                    {!!groupingsConfig?.grouping1Label && allocSidebarGroupingOptions.g1.length > 0 && (
                      <>
                        <label className="multi-trade-alloc-sidebar-label">{groupingsConfig.grouping1Label}</label>
                        <FilterableSelect
                          className="multi-trade-alloc-sidebar-filterable"
                          value={newAllocG1}
                          options={allocSidebarGroupingOptions.g1}
                          onChange={(v) => { setNewAllocG1(v); setNewAllocG2(''); setNewAllocG3(''); }}
                        />
                      </>
                    )}
                    {!!groupingsConfig?.grouping2Label && allocSidebarGroupingOptions.g2.length > 0 && (
                      <>
                        <label className="multi-trade-alloc-sidebar-label">{groupingsConfig.grouping2Label}</label>
                        <FilterableSelect
                          className="multi-trade-alloc-sidebar-filterable"
                          value={newAllocG2}
                          options={allocSidebarGroupingOptions.g2}
                          onChange={(v) => { setNewAllocG2(v); setNewAllocG3(''); }}
                        />
                      </>
                    )}
                    {!!groupingsConfig?.grouping3Label && allocSidebarGroupingOptions.g3.length > 0 && (
                      <>
                        <label className="multi-trade-alloc-sidebar-label">{groupingsConfig.grouping3Label}</label>
                        <FilterableSelect
                          className="multi-trade-alloc-sidebar-filterable"
                          value={newAllocG3}
                          options={allocSidebarGroupingOptions.g3}
                          onChange={(v) => setNewAllocG3(v)}
                        />
                      </>
                    )}
                    <button
                      className="btn btn-primary btn-sm multi-trade-alloc-sidebar-add-btn"
                      onClick={handleSubmitAllocColumn}
                      disabled={!newAllocEntity}
                    >
                      {t('multiTrade.allocations.add')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>}

        {/* Result banner */}
        {submitResults && (
          <div className={`multi-trade-result ${submitResults.failed > 0 ? 'error' : 'success'}`}>
            {submitResults.failed === 0
              ? t('multiTrade.bookSuccess', { count: submitResults.success })
              : t('multiTrade.partialSuccess', {
                  success: submitResults.success,
                  total: submitResults.success + submitResults.failed,
                  failed: submitResults.failed,
                })
            }
          </div>
        )}

        {/* KAN-216: Confirmation match suggestions */}
        {confirmationMatches.length > 0 && (
          <div className="multi-trade-confirmation-matches">
            <div className="multi-trade-confirmation-matches-header">
              <strong>{t('multiTrade.confirmationMatches.title')}</strong>
              <p className="multi-trade-confirmation-matches-subtitle">
                {t('multiTrade.confirmationMatches.subtitle')}
              </p>
            </div>
            <table className="multi-trade-confirmation-matches-table">
              <thead>
                <tr>
                  <th>{t('multiTrade.confirmationMatches.colTrade')}</th>
                  <th>{t('multiTrade.confirmationMatches.colConfirmation')}</th>
                  <th>{t('multiTrade.confirmationMatches.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {confirmationMatches.map((entry, idx) => (
                  <tr key={idx}>
                    <td>{entry.tradeNumber}</td>
                    <td>
                      {entry.match.type === 'single'
                        ? `${entry.match.counterpartyName}, ${entry.match.receivedDate ? entry.match.receivedDate.split('-').reverse().join('/') : ''}, ${t('multiTrade.confirmationMatches.ref')} ${entry.match.bankTradeNumber}`
                        : t('multiTrade.confirmationMatches.multipleMatches')
                      }
                    </td>
                    <td>
                      {entry.match.type === 'single' ? (
                        entry.status === 'pending' ? (
                          <>
                            <button className="btn btn-primary btn-xs" onClick={() => handleReprocessMatch(idx)}>
                              {t('multiTrade.confirmationMatches.reprocess')}
                            </button>
                            <button className="btn btn-secondary btn-xs" onClick={() => setConfirmationMatches(prev => prev.filter((_, i) => i !== idx))} style={{ marginLeft: '4px' }}>
                              {t('multiTrade.confirmationMatches.skip')}
                            </button>
                          </>
                        ) : entry.status === 'processing' ? (
                          <span className="text-muted">{t('tradeBooking.messages.loading')}</span>
                        ) : entry.status === 'success' ? (
                          <span className="text-success">{t('tradeBooking.messages.confirmationReprocessSuccess')}</span>
                        ) : (
                          <span className="text-error">{t('tradeBooking.messages.confirmationReprocessFailed')}</span>
                        )
                      ) : (
                        <span className="text-muted">{t('multiTrade.confirmationMatches.reviewInExceptions')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="multi-trade-confirmation-matches-actions">
              {confirmationMatches.some(m => m.match.type === 'single' && m.status === 'pending') && (
                <button className="btn btn-primary btn-sm" onClick={handleReprocessAll}>
                  {t('multiTrade.confirmationMatches.reprocessAll')}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={handleSkipAllMatches}>
                {confirmationMatches.some(m => m.status === 'pending')
                  ? t('multiTrade.confirmationMatches.skipAllClose')
                  : t('common.close')}
              </button>
            </div>
          </div>
        )}

        {/* Footer actions (hidden when confirmation matches are showing — trades already booked) */}
        {confirmationMatches.length === 0 && (
          <div className="multi-trade-footer">
            <button
              className="btn btn-secondary"
              onClick={handleRemoveSelected}
              disabled={selectedCount === 0 || isSubmitting}
            >
              {t('multiTrade.removeSelected')}
            </button>
            <div className="multi-trade-footer-right">
              <button
                className="btn btn-secondary"
                onClick={handleSaveDraft}
                disabled={trades.length === 0 || isSubmitting || draftSaving}
              >
                {draftSaving ? '...' : t('multiTrade.saveDraft')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBookAll}
                disabled={selectedCount === 0 || errorCount > 0 || isSubmitting}
              >
                {t('multiTrade.bookAll')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submission overlay */}
      {isSubmitting && (
        <div className="multi-trade-submission-overlay">
          <div className="multi-trade-submission-spinner">
            <div className="section-spinner" />
            <p>{t(`multiTrade.submitting.${submitProgress.type}`, {
              current: submitProgress.current,
              total: submitProgress.total,
            })}</p>
          </div>
        </div>
      )}

      {/* Failure alert modal */}
      <AlertModal
        isOpen={submitAlert !== null}
        onClose={() => setSubmitAlert(null)}
        title={submitAlert?.title || ''}
        message={submitAlert?.message || ''}
        type="error"
      />

      {/* Mode switch confirmation */}
      <ConfirmDialog
        isOpen={pendingModeSwitch !== null}
        title={t('multiTrade.modeSwitchWarningTitle')}
        message={
          gridMode === 'allocations' && pendingModeSwitch === 'normal'
            ? t('multiTrade.modeSwitchWarningAllocToNormal')
            : t('multiTrade.modeSwitchWarning')
        }
        confirmLabel={t('multiTrade.modeSwitchConfirm')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmModeSwitch}
        onCancel={() => setPendingModeSwitch(null)}
        type="warning"
      />

      {/* Draft name prompt */}
      {showDraftNamePrompt && (
        <div className="multi-trade-draft-name-overlay">
          <div className="multi-trade-draft-name-dialog">
            <h4>{t('multiTrade.draftName')}</h4>
            <input
              type="text"
              className="form-control"
              placeholder={t('multiTrade.draftNamePlaceholder')}
              value={draftNameInput}
              onChange={(e) => setDraftNameInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateDraft(); }}
              autoFocus
            />
            <div className="multi-trade-draft-name-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDraftNamePrompt(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleCreateDraft} disabled={!draftNameInput.trim()}>
                {t('multiTrade.saveDraft')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft picker */}
      {showDraftPicker && (
        <div className="multi-trade-draft-name-overlay">
          <div className="multi-trade-draft-picker-dialog">
            <h4>{t('multiTrade.loadDraft')}</h4>
            {drafts.length === 0 ? (
              <p className="multi-trade-draft-empty">{t('multiTrade.noDrafts')}</p>
            ) : (
              <ul className="multi-trade-draft-list">
                {drafts.map(d => (
                  <li key={d.id} className="multi-trade-draft-item">
                    <div className="multi-trade-draft-item-info" onClick={() => handleLoadDraft(d.id)}>
                      <strong>{d.name}</strong>
                      {d.lockedAt && (Date.now() - new Date(d.lockedAt).getTime()) < 15 * 60 * 1000 && (
                        <span className="multi-trade-draft-lock-indicator">
                          <Lock size={11} />
                          {t('multiTrade.draftLockedBy', { name: d.lockedByName })}
                        </span>
                      )}
                      <span className="multi-trade-draft-item-meta">
                        {t(`multiTrade.modes.${d.mode}`)} — {t('multiTrade.draftRowCount', { count: d.rowCount })} — {d.updatedByName}
                        {d.updatedAt && (() => {
                          const dt = new Date(d.updatedAt);
                          const dd = String(dt.getDate()).padStart(2, '0');
                          const mm = String(dt.getMonth() + 1).padStart(2, '0');
                          const yyyy = dt.getFullYear();
                          return ` — ${dd}/${mm}/${yyyy}`;
                        })()}
                      </span>
                    </div>
                    <button
                      className="multi-trade-draft-delete-btn"
                      title={t('common.delete')}
                      onClick={(e) => { e.stopPropagation(); setPendingDraftDelete(d.id); setShowDraftPicker(false); }}
                    >
                      &times;
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="multi-trade-draft-name-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDraftPicker(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load draft confirmation (unsaved data warning) */}
      <ConfirmDialog
        isOpen={pendingDraftLoad !== null}
        title={t('multiTrade.loadDraft')}
        message={t('multiTrade.loadDraftConfirm')}
        confirmLabel={t('common.ok')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { if (pendingDraftLoad) executeLoadDraft(pendingDraftLoad); setPendingDraftLoad(null); }}
        onCancel={() => { setPendingDraftLoad(null); setShowDraftPicker(true); }}
        type="warning"
      />

      {/* Delete draft confirmation */}
      <ConfirmDialog
        isOpen={pendingDraftDelete !== null}
        title={t('common.delete')}
        message={t('multiTrade.deleteDraftConfirm')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { if (pendingDraftDelete) handleDeleteDraft(pendingDraftDelete); }}
        onCancel={() => { setPendingDraftDelete(null); setShowDraftPicker(true); }}
        type="warning"
      />

      {/* Paste overwrite confirmation */}
      <ConfirmDialog
        isOpen={pendingPaste !== null}
        title={t('multiTrade.paste.overwriteTitle')}
        message={t('multiTrade.paste.overwriteConfirm', { count: pendingPaste?.targetRowIndexes?.length ?? 0 })}
        confirmLabel={t('common.ok')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => { if (pendingPaste) applyPasteValues(pendingPaste.fields, pendingPaste.values, pendingPaste.targetRowIndexes, pendingPaste.mismatchRows); }}
        onCancel={() => setPendingPaste(null)}
        type="warning"
      />

      {/* Draft toast notification */}
      <Toast
        message={draftToast?.message || ''}
        type={draftToast?.type || 'success'}
        isVisible={draftToast !== null}
        onClose={() => setDraftToast(null)}
      />

      {/* Copy splits inline popup */}
      {pendingCopy && (
        <div className="mt-copy-splits-popup" ref={copySplitsPopupRef} style={{ top: pendingCopy.y, left: pendingCopy.x }}>
          <span className="mt-copy-splits-label">{t('multiTrade.copySplitsPrompt')}</span>
          <div className="mt-copy-splits-actions">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => copyRowAsStandalone(pendingCopy.rowIndex)}>
              {t('multiTrade.copySplitsNo')}
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => copyRowWithSplits(pendingCopy.rowIndex)}>
              {t('multiTrade.copySplitsYes')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiTradeModal;
